// ── BILLING SUMMARY SECTION ───────────────────────────────────────────────────
// Monthly comprehensive client doc. Lifted out of BillingTab into a self-
// contained component, mounted under InvoicesTab → Billing Summary.
//
// Stage 3 additions:
//   1. Retainer logic moved from client.billingMethod to project.hasRetainer.
//      The Krupka Moskito and Kubik Ptarmigan projects show no retainer math
//      until/unless the owner toggles hasRetainer on the project record. Hoback
//      is the only project on a retainer today.
//   2. Hourly narratives auto-attach. When a Final hours_invoices row exists
//      for the project + month, its Claude category narratives flow into the
//      payload (and the PDF will render them under Design Services once the
//      Python generator is updated to consume them).
//   3. Sent / paid lifecycle persisted to the new billing_summaries table.
//      History list at the top so the bookkeeper has the audit trail they
//      have been keeping in spreadsheets.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import {
  appliedCreditsStateEqual,
  calcRetainerBalance,
  calcPaidForDocument,
  derivePaidStatus,
  paymentEntriesForDocument,
  assemblyNet,
  assemblyClient,
  itemDepositAmount,
  ccFeeMul,
  proposalGrandTotal,
  parseQty,
  fmtMoney,
  hasAdminAccess,
  calcBillingSummaryTotalDue,
  buildProposalPayloadPure,
  applyReimbursableMarkup,
  receiptMarkupPct,
  receiptSummaryDescription,
  buildSummaryReceiptLines,
  applyExpenseGroupsToReceiptLines,
  buildHoursInvoiceRegenPayload,
  receiptIsAbsorbed,
  normalizeExpensesDisplay,
  DEFAULT_CONSOLIDATED_EXPENSES_LABEL,
  calcCreditAvailableBalance,
  CLIENT_CREDIT_SOURCE_TYPES,
  creditPrintLabel,
  creditsSectionLabel,
  clientCreditToRow,
  rowToClientCredit,
  billingSummaryStoragePath,
  proposalStoragePath,
  activeProjects,
  invoiceCcFeeTot,
} from '../utils';
import PaymentsPanel from './PaymentsPanel';
import OffCyclePackageModal from './OffCyclePackageModal';
// (RecordClientPaymentWizard moved to BillingHistorySection 2026-07-02 —
// payments are recorded from Billing History's package preview now; this
// workbench is build/send only.)
function fmtSentDate(iso) {
  if (!iso) return '';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return m[1] + '-' + m[2] + '-' + m[3];
    var d = new Date(s);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch (e) { return ''; }
}

function fmtFull(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    // YYYY-MM-DD (date column) — parse as local to avoid the UTC->local
    // shift that displays "2025-11-25" as Nov 24 in Pacific time.
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return String(iso).slice(0, 10); }
}

const inp = {
  padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff',
  boxSizing: 'border-box',
};

const lbl = { fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 };
const rmBtn = { padding: '4px 8px', background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 5, cursor: 'pointer', color: '#888', fontSize: 12, flexShrink: 0 };
const addBtn = { padding: '5px 13px', background: '#f5f5f5', border: '1px dashed #ccc', borderRadius: 5, fontSize: 11, cursor: 'pointer', marginTop: 4 };

function buildInvoiceNumber(projLabel, year, month) {
  return `${projLabel}-${year}-${String(month).padStart(2, '0')}`;
}

// Friendly NAME for an invoice on the procurement section of the
// billing-summary cover (2026-06-28 — Cimon: 'Final Invoice' alone is
// useless; show the parent proposal's name so each FIN row reads as
// 'HQ LED Replacement Lights' instead of three identical 'Final
// Invoice' labels). Deposit invoices get the same treatment — the
// proposal name describes what the deposit covers. Reimbursable +
// designer-hours invoices have no parent proposal, so they keep
// their type label.
//
// Used by both buildBsPayload (draft preview) and buildSummaryPayload
// (sent-package) to keep the two payloads in lockstep.
function invoiceFriendlyName(invoice, proposals) {
  const t = String(invoice.invoiceType || invoice.invoice_type || '').toLowerCase();
  // Names-first (2026-07-18): DEP + FIN rows read the item name(s) off
  // the invoice's own line_items snapshot — the same names the client
  // sees on the invoice PDF itself ("BDDW Dresser", "Kumiko Art
  // Screen"). FINs are PO-scoped and usually carry no proposal_id, so
  // the parent-proposal lookup alone left every FIN row as a bare
  // "Final Invoice" on the cover PDF and the workbench table. The
  // parent proposal's name stays as the fallback when the snapshot has
  // no usable names (legacy rows), then the type label.
  const lineNames = (invoice.lineItems || invoice.line_items || [])
    .map(l => String((l && (l.item || l.productName)) || '').trim())
    .filter(Boolean);
  const invProposalId = invoice.proposalId || invoice.proposal_id || '';
  const parentProposal = invProposalId
    ? (proposals || []).find(p => String(p.id) === String(invProposalId))
    : null;
  const proposalName = parentProposal
    ? (parentProposal.proposal_name || parentProposal.proposalName || '')
    : '';
  if (t === 'deposit' || t === 'final') {
    if (lineNames.length === 1) return lineNames[0];
    if (lineNames.length > 1)  return lineNames[0] + ' + ' + (lineNames.length - 1) + ' more';
    return proposalName || (t === 'deposit' ? 'Deposit Invoice' : 'Final Invoice');
  }
  if (t === 'reimbursable')   return 'Reimbursable Invoice';
  if (t === 'designer_hours') return 'Designer Hours Invoice';
  return invoice.invoiceRef || invoice.invoice_ref || 'Invoice';
}

// Retainer Request input — currency-formatted text field. Maintains the
// numeric value via the `value` prop (a number); displays it formatted as
// $X,XXX.XX while not focused. While focused, the user can type freely;
// the on-change parser strips $ and commas. On blur the value is re-formatted
// for display. Underlying state in the parent stays numeric.
function RetainerRequestInput(props) {
  function fmt(n) {
    var num = Number(n);
    if (!isFinite(num)) return '';
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function parse(s) {
    if (s == null) return 0;
    var stripped = String(s).replace(/[$,\s]/g, '');
    var num = parseFloat(stripped);
    return isFinite(num) ? num : 0;
  }
  var [focused, setFocused] = React.useState(false);
  var [draft, setDraft] = React.useState('');
  var displayed = focused ? draft : fmt(props.value);
  return React.createElement('input', {
    type: 'text',
    inputMode: 'decimal',
    value: displayed,
    onFocus: function() {
      setFocused(true);
      // Show the raw numeric value (no $, no commas) on focus so the user
      // can edit it without fighting the formatter. fmt() output would
      // otherwise need them to delete the $ before typing.
      setDraft(props.value === 0 ? '' : String(props.value));
    },
    onChange: function(e) {
      var raw = e.target.value;
      setDraft(raw);
      props.onChange(parse(raw));
    },
    onBlur: function() {
      setFocused(false);
      // Snap the parent to the parsed numeric — onChange already wrote it,
      // so this is just to clear the local draft.
      setDraft('');
    },
    'data-testid': 'retainer-request-input',
    style: { width: 130, padding: '4px 10px', fontSize: 13, fontWeight: 600, textAlign: 'right', border: '0.5px solid #D0D0CC', borderRadius: 4, fontFamily: 'inherit' },
  });
}

export default function BillingSummarySection(props) {
  const ctx = useAppContext();
  const projects = ctx.projects || [];
  const clients  = ctx.clients  || [];
  const proposals = ctx.proposals || [];
  const setProposals = ctx.setProposals;
  const allItems = ctx.items || [];
  const invoices = ctx.invoices || [];
  const setInvoices = ctx.setInvoices;
  const receipts = ctx.receipts || [];
  const setReceipts = ctx.setReceipts;
  const userEmail = ctx.userEmail || '';
  const isOwner   = !!ctx.isOwner;
  const retainerLedger = ctx.retainerLedger || [];
  const saveRetainerEntry = ctx.saveRetainerEntry;
  const deleteRetainerEntry = ctx.deleteRetainerEntry;
  const saveInvoice = ctx.saveInvoice;
  const firmSettings = ctx.firmSettings || {};
  const clientPayments        = ctx.clientPayments        || [];
  const paymentAllocations    = ctx.paymentAllocations    || [];
  const recordClientPayment   = ctx.recordClientPayment;
  const removePaymentAllocation = ctx.removePaymentAllocation;
  // Client credit ledger — drives the AVAILABLE CREDITS picker in the
  // procurement section. Live state so applying/unapplying updates the
  // picker without a refetch.
  const clientCreditLedger    = ctx.clientCreditLedger    || [];
  const setClientCreditLedger = ctx.setClientCreditLedger;

  const [offCycleOpen, setOffCycleOpen] = useState(false);
  const [bsProjId, setBsProjId] = useState('');
  const [bsMonth,  setBsMonth]  = useState(new Date().getMonth() + 1);
  const [bsYear,   setBsYear]   = useState(new Date().getFullYear());

  const [bsHoursInvoice,    setBsHoursInvoice]    = useState(null);
  const [bsHoursLoading,    setBsHoursLoading]    = useState(false);
  const [bsDesignHours,     setBsDesignHours]     = useState('');
  const [bsDesignAmount,    setBsDesignAmount]    = useState('');
  const [bsHoursNarratives, setBsHoursNarratives] = useState([]);  // [{category, hours, amount, narrative}]

  // Andra Birkerts is only billed on retainer-client packages (currently
  // Kubik). Empty by default — pre-filled below when the bookkeeper picks a
  // retainer project.
  const [bsAdditionalServices, setBsAdditionalServices] = useState([]);
  // Free-text expense rows. Reimbursables backed by receipts now go
  // through Receipts → Bill Reimbursables, which creates a separate
  // RE invoice that auto-appears in the C2 invoice list. These rows
  // remain for the rare unreceipted entry (stipend, unreceipted small
  // item, narrative-only line).
  const [bsExpenses, setBsExpenses]       = useState([{ description: '', amount: '' }]);
  // Retainer request override: null means "use auto-computed default";
  // a number means the bookkeeper has explicitly set it. Persisted on
  // save (B = preserve what was sent to client). Resets to null when
  // project/month changes; the project+period lookup useEffect sets it
  // from any pre-existing saved row.
  const [bsRetainerRequestOverride, setBsRetainerRequestOverride] = useState(null);
  const [bsProcurement, setBsProcurement] = useState({ proposalTotal: '', depositRequested: '', invoicesDue: '' });

  // Proposals on the summary — per-row overrides so the bookkeeper can
  // tweak the displayed sent date without editing the proposal record itself
  // (proposal.sent_at stays as the audit trail).
  //   bsProposalDateOverrides:  { proposalId → 'YYYY-MM-DD' }   editable date per row
  //   bsProposalsExcluded:      Set of proposalIds to hide from this summary
  const [bsProposalDateOverrides, setBsProposalDateOverrides] = useState({});
  const [bsProposalsExcluded,     setBsProposalsExcluded]     = useState({});
  const [bsInvoicesExcluded,      setBsInvoicesExcluded]      = useState({});

  // Reimbursable receipts the bookkeeper has ticked on the current draft.
  // Set of receipt ids. Hydrated from bsSavedRecord.selected_receipt_ids
  // on draft load, persisted as Array.from() on every save. On Mark Sent,
  // each id gets receipts.billed_on_summary_id = summary.id written.
  // The selection stores IDS ONLY — never descriptions or amounts. Editing
  // a receipt on the Receipts tab AFTER selection propagates to the next
  // render of this summary.
  const [bsReimbursableSelections, setBsReimbursableSelections] = useState(() => new Set());
  // Statement expense presentation (2026-08-23 billing simplification):
  // when consolidate is on, the cover's PROJECT EXPENSES group renders as
  // ONE line (label + summed amount) instead of itemized rows. Persisted
  // on billing_summaries.expenses_display; presentation only — totals are
  // computed from the same underlying lines either way.
  const [bsExpensesDisplay, setBsExpensesDisplay] = useState({ consolidate: false, label: '', groups: [] });

  // Building a combined line (2026-09-04, Cimon: "check these receipts — and
  // then accept so the line is created with the receipts"). The old flow ran
  // backwards: "+ New line" made an EMPTY group, then each receipt had to be
  // reassigned one dropdown at a time, and a freshly made line sat there
  // reading "0 receipts" with no way to finish. These hold the in-progress
  // pick set and its name until the line is added; neither is persisted.
  const [bsGroupPicks,      setBsGroupPicks]      = useState(() => new Set());
  const [bsGroupDraftLabel, setBsGroupDraftLabel] = useState('');

  function addGroupFromPicks() {
    if (bsGroupPicks.size === 0) return;
    const ids = Array.from(bsGroupPicks);
    setBsExpensesDisplay(prev => ({
      ...prev,
      groups: [...(prev.groups || []), {
        // randomUUID, not Date.now(): two adds in the same millisecond would
        // share an id, and rename/remove/assign would hit both lines.
        id: 'g-' + (crypto.randomUUID ? crypto.randomUUID() : Date.now() + '-' + Math.random()),
        label: bsGroupDraftLabel.trim(),
        receiptIds: ids,
      }],
    }));
    // Putting a receipt on a line IS a decision to bill it, so make sure it
    // is ticked. Keeps membership and inclusion from drifting apart.
    setBsReimbursableSelections(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
    setBsGroupPicks(new Set());
    setBsGroupDraftLabel('');
  }

  // Client credit applications on the current draft.
  //   Map<sourceCreditId, { amount, ledgerRowId, label }>
  // ledgerRowId is null for unsaved applications; set to the
  // applied_to_billing_summary row's id once persisted. The persist
  // diff in persistRecord uses this to decide INSERT vs UPDATE vs
  // DELETE relative to whatever's in client_credit_ledger for this
  // summary. Hydrated on draft load from clientCreditLedger.
  //
  // `label` is what the row is CALLED on the client statement
  // (2026-09-07, Cimon: "it's not really a credit — I just never ordered
  // those beds"). It defaults per source type via creditPrintLabel and
  // persists in the applied row's existing `notes` column, so wording is
  // editable per application with no schema change. `notes` previously
  // duplicated the source credit's reference, which is already reachable
  // through source_credit_id — nothing read it.
  const [bsAppliedCredits, setBsAppliedCredits] = useState(() => ({}));

  const [bsSummaryGenerating, setBsSummaryGenerating] = useState(false);
  // Live preview state — debounced cover-PDF regeneration into the right pane.
  const [bsLivePdfUrl,     setBsLivePdfUrl]     = useState(null);
  // The period the DISPLAYED blob was rendered for (2026-08-15) — set at
  // fetch success, so the pane header names what's actually on screen
  // even while a newer period's render is still in flight.
  const [bsLivePdfPeriod,  setBsLivePdfPeriod]  = useState('');
  const [bsLivePdfLoading, setBsLivePdfLoading] = useState(false);
  const [bsLivePdfError,   setBsLivePdfError]   = useState('');

  // F3: which doc is currently being previewed in the right pane.
  // 'cover' = the live-regen cover. Otherwise a bundled item.
  const [bsPreviewSel,        setBsPreviewSel]        = useState({ type: 'cover' });
  const [bsBundledPdfUrl,     setBsBundledPdfUrl]     = useState(null);
  const [bsBundledPdfLoading, setBsBundledPdfLoading] = useState(false);
  const [bsBundledPdfError,   setBsBundledPdfError]   = useState('');
  // Latest-fetch token. Each bundled-doc fetch captures the value at start
  // and bails out if it doesn't match by the time the response arrives,
  // so a slow earlier click can't overwrite a faster later one.
  const bsBundledFetchToken = useRef(0);
  const [bsSummaryError,      setBsSummaryError]      = useState('');
  const [bsSavedRecord,       setBsSavedRecord]       = useState(null);  // the billing_summaries row we wrote
  const [bsLastSavedAt,       setBsLastSavedAt]       = useState(null);  // local timestamp for "Saved at HH:MM"
  const [bsDraftsList,        setBsDraftsList]        = useState([]);    // all status=draft rows for the picker
  const [bsDraftPickerOpen,   setBsDraftPickerOpen]   = useState(false);
  const [bsDraftConflict,     setBsDraftConflict]     = useState(null);  // {projectLabel, year, month, id} of existing draft on this project
  // Autosave-race guards (2026-07-27). bsRecordLoadedKey names the
  // (project|year|month) whose saved-record load has RESOLVED — until it
  // matches the current period, autosave must not persist: bsSavedRecord
  // is still null even when a SENT summary exists in the DB, and a draft
  // persist would silently downgrade it and overwrite its snapshot
  // columns with the form's defaults. bsSavedRecordRef mirrors the LATEST
  // record for persistRecord's terminal-status refusal — a debounce timer
  // armed just before a send completes carries a stale render closure,
  // and the closure's bsSavedRecord predates the sent flip.
  const [bsRecordLoadedKey,   setBsRecordLoadedKey]   = useState('');
  const bsSavedRecordRef = useRef(null);
  useEffect(() => { bsSavedRecordRef.current = bsSavedRecord; }, [bsSavedRecord]);

  // ── LOOKUP ──
  // When project / month / year changes, look up the finalized hours invoice
  // for that period and pre-fill design hours/amount + narratives.
  async function lookupHoursInvoice(projId, month, year) {
    const proj = projects.find(pr => String(pr.id) === String(projId));
    if (!proj) return;
    const invoiceNumber = buildInvoiceNumber(proj.label, year, month);
    setBsHoursLoading(true);
    // The whole body runs inside try/finally (2026-08-16): since the
    // live preview GATES on bsHoursLoading (2026-08-15), a throw between
    // setBsHoursLoading(true) and (false) — a malformed legacy snapshot,
    // a transport error — would otherwise pin the flag true and silently
    // block the preview forever.
    try {
    const { data } = await supabase.from('hours_invoices')
      .select('*').eq('invoice_number', invoiceNumber).maybeSingle();
    setBsHoursInvoice(data || null);
    if (data?.status === 'Final') {
      setBsDesignHours(data.total_hours != null ? String(data.total_hours) : '');
      setBsDesignAmount(data.total_amount != null ? String(data.total_amount) : '');
      // Auto-attach Claude narratives — pulled from the saved hours invoice
      const summaries = data.summaries || {};
      const cats = data.categories_snapshot || [];
      const adjusted = data.adjusted_hours || {};
      const narratives = cats
        .filter(c => (c.totalHours || 0) > 0)
        .map(c => {
          const effEntries = (c.entries || []).map(e => {
            const k = `${c.name}__${e.togglName}`;
            const effHrs = adjusted[k] != null ? Number(adjusted[k]) : e.hours;
            return { hrs: effHrs, amt: Math.round(effHrs * e.rate * 100) / 100 };
          });
          const effHours  = Math.round(effEntries.reduce((s, x) => s + x.hrs, 0) * 100) / 100;
          const effAmount = Math.round(effEntries.reduce((s, x) => s + x.amt, 0) * 100) / 100;
          return {
            category:  c.name,
            hours:     effHours,
            amount:    effAmount,
            narrative: summaries[c.name] || '',
          };
        });
      setBsHoursNarratives(narratives);
    } else {
      setBsDesignHours('');
      setBsDesignAmount('');
      setBsHoursNarratives([]);
    }
    // (2026-08-15) The legacy `setBsSavedRecord(null)` reset that lived
    // here is gone: since the keyed load effect (2026-07-27) owns
    // bsSavedRecord, this late-resolving lookup was RACING it — nulling
    // the just-loaded draft record whenever the lookup resolved after
    // the load, which dropped the DRAFT banner and churned the memo
    // chain (bsInvoicesList deps bsSavedRecord) mid-preview.
    } catch (e) {
      console.error('[bs-hours-lookup]', e);
    } finally {
      setBsHoursLoading(false);
    }
  }

  // Look up an existing billing_summaries record for this project+month+year
  // and rehydrate the form. Without this, picking a project+month with a
  // saved draft would show "DRAFT" in the banner but leave the form empty,
  // and any save would overwrite the saved data with whatever's currently
  // typed. (Used to live in loadFromHistory + the in-section history list,
  // both removed when Sent Packages took over.)
  useEffect(() => {
    if (!bsProjId) return;
    const proj = projects.find(pr => String(pr.id) === String(bsProjId));
    if (!proj) return;
    // Block autosave until THIS period's load resolves (2026-07-27 race
    // fix). The key is captured per-run, so a slow stale load can only
    // ever publish its own (now non-current) key — never unblock the
    // current period with another period's record.
    const loadKey = bsProjId + '|' + bsYear + '|' + bsMonth;
    setBsRecordLoadedKey('');
    (async () => {
      // With the (project, year, month) UNIQUE constraint dropped, multiple
      // billing_summaries can match this period. Prefer the active draft
      // (only one draft per project ever exists -- partial unique index
      // billing_summaries_one_draft_per_project_idx). If no draft, fall
      // back to the most recent sent/paid row so the bookkeeper sees
      // historical context for this period.
      // summary_type filter (2026-08-31): off-cycle packages share the
      // same (project, year, month), so without it the workbench claimed
      // an off_cycle row as "the" monthly — Aug Hoback showed SENT for a
      // monthly that was never built (user report: "I definitely haven't
      // finalized anything"). This workbench is the MONTHLY editor;
      // off-cycle rows are OffCyclePackageModal territory.
      let data = null;
      const draftQ = await supabase.from('billing_summaries')
        .select('*')
        .eq('project_label', proj.label)
        .eq('year', bsYear)
        .eq('month', bsMonth)
        .eq('summary_type', 'monthly')
        .eq('status', 'draft')
        .maybeSingle();
      data = draftQ.data || null;
      if (!data) {
        const histQ = await supabase.from('billing_summaries')
          .select('*')
          .eq('project_label', proj.label)
          .eq('year', bsYear)
          .eq('month', bsMonth)
          .eq('summary_type', 'monthly')
          .order('updated_at', { ascending: false })
          .limit(1);
        data = (histQ.data && histQ.data[0]) || null;
      }
      setBsSavedRecord(data);
      if (data) {
        // Rehydrate form fields from the saved row. Hours-related fields
        // (bsDesignHours, bsDesignAmount, bsHoursNarratives) come from
        // lookupHoursInvoice in its own flow, so we don't overwrite them
        // here -- the hours_invoices row is authoritative for those.
        const addl = typeof data.additional_services === 'string'
          ? (function() { try { return JSON.parse(data.additional_services); } catch (e) { return []; } })()
          : (data.additional_services || []);
        setBsAdditionalServices(addl.length > 0 ? addl : [{ name: '', amount: '' }]);
        const exp = typeof data.expenses === 'string'
          ? (function() { try { return JSON.parse(data.expenses); } catch (e) { return []; } })()
          : (data.expenses || []);
        setBsExpenses(exp.length > 0
          ? exp.map(e => ({ description: e.description || '', amount: e.amount || '' }))
          : [{ description: '', amount: '' }]);
        // DRAFTS re-derive the retainer request live (2026-08-05): every
        // autosave stores the then-effective request, and hydrating that
        // snapshot as an OVERRIDE permanently pinned the field — Cimon's
        // July draft kept asking $0 while the balance ran -$12,035.34,
        // because a stale stored 0 beat the new shortfall rule. Only
        // SENT/PAID rows hydrate the stored value (display parity with
        // what the client received). On a draft the field stays live —
        // type in it to override for this session, Reset to re-derive.
        if (data.retainer_request != null && data.status !== 'draft') {
          setBsRetainerRequestOverride(parseFloat(data.retainer_request));
        } else {
          setBsRetainerRequestOverride(null);
        }
        // Hydrate the statement expense-presentation setting (2026-08-23).
        setBsExpensesDisplay(normalizeExpensesDisplay(data.expenses_display));
        // Hydrate the reimbursable-receipt selection from the saved row.
        // jsonb round-trips as a JS array; tolerate string for legacy /
        // edge-case rows. IDS ONLY — descriptions and amounts re-derive
        // from the live receipts state at render time.
        var savedIds = data.selected_receipt_ids;
        if (typeof savedIds === 'string') {
          try { savedIds = JSON.parse(savedIds); } catch (e) { savedIds = []; }
        }
        setBsReimbursableSelections(new Set(Array.isArray(savedIds) ? savedIds : []));
        // Hydrate applied-credit selections from client_credit_ledger.
        // Filter for applied_to_billing_summary rows whose targetSummaryId
        // matches the loaded summary id. Map keyed by sourceCreditId so the
        // picker can render checked state + amount per original credit.
        // ledgerRowId is the existing row's id — persistRecord's diff
        // pivots on it (present → UPDATE/keep, absent → INSERT).
        var hydrated = {};
        (clientCreditLedger || []).forEach(function(e) {
          if (!e || e.entryType !== 'applied_to_billing_summary') return;
          if (String(e.targetSummaryId || '') !== String(data.id)) return;
          if (!e.sourceCreditId) return;
          var hsrc = (clientCreditLedger || []).find(function(o) {
            return o && String(o.id) === String(e.sourceCreditId);
          });
          // `notes` carries the statement wording (see bsAppliedCredits).
          // Rows written BEFORE 2026-09-07 stored the source credit's
          // reference there instead; that is not a label, so ignore it
          // and fall back to the per-source-type default. Anything else
          // is a wording the bookkeeper typed and must survive reload.
          var hnotes = String(e.notes || '').trim();
          if (hsrc && hnotes === String(hsrc.reference || '').trim()) hnotes = '';
          hydrated[String(e.sourceCreditId)] = {
            amount: Math.abs(parseFloat(e.amount) || 0),
            ledgerRowId: e.id || null,
            label: hnotes || creditPrintLabel(hsrc && hsrc.entryType),
          };
        });
        setBsAppliedCredits(hydrated);
      } else {
        // No saved record: clear back to defaults so a previous project's
        // values don't bleed into a fresh form.
        setBsAdditionalServices([{ name: '', amount: '' }]);
        setBsExpenses([{ description: '', amount: '' }]);
        setBsRetainerRequestOverride(null);
        setBsReimbursableSelections(new Set());
        setBsAppliedCredits({});
        setBsExpensesDisplay({ consolidate: false, label: '', groups: [] });
      }

      // Conflict check: does any OTHER draft exist for this project (a
      // different month)? The DB partial-unique-index would reject a save
      // attempt; we surface that early so the bookkeeper can switch
      // instead of filling out a doomed form.
      const { data: existingDraft } = await supabase.from('billing_summaries')
        .select('id, project_label, year, month')
        .eq('project_label', proj.label)
        .eq('status', 'draft')
        .maybeSingle();
      if (existingDraft && !(existingDraft.year === bsYear && existingDraft.month === bsMonth)) {
        setBsDraftConflict(existingDraft);
      } else {
        setBsDraftConflict(null);
      }
      setBsRecordLoadedKey(loadKey);
    })().catch(e => {
      // Publish the key even on failure (2026-08-16): the live preview
      // gates on it, so an unhandled load error would otherwise block
      // rendering silently and forever.
      console.error('[bs-period-load]', e);
      setBsRecordLoadedKey(loadKey);
    });
  }, [bsProjId, bsMonth, bsYear, projects]);

  // ── DERIVED ──
  const bsSelProj         = projects.find(pr => String(pr.id) === String(bsProjId));

  // Pre-fill Andra Birkerts row only on retainer projects (Kubik). When the
  // bookkeeper picks a non-retainer project and the Andra row is still blank,
  // remove it so non-Kubik clients don't see "Andra Birkerts" as a default.
  useEffect(() => {
    if (!bsSelProj) return;
    const isRetainer = !!bsSelProj.hasRetainer;
    const onlyEmptyAndra = bsAdditionalServices.length === 1
      && bsAdditionalServices[0].name === 'Andra Birkerts Design Consulting'
      && !bsAdditionalServices[0].amount;
    if (isRetainer && bsAdditionalServices.length === 0) {
      setBsAdditionalServices([{ name: 'Andra Birkerts Design Consulting', amount: '' }]);
    } else if (!isRetainer && onlyEmptyAndra) {
      setBsAdditionalServices([]);
    }
  }, [bsProjId]); // eslint-disable-line react-hooks/exhaustive-deps
  const bsClient          = bsSelProj ? clients.find(c => c.id === bsSelProj.clientId) : null;
  const bsMonthName       = ['January','February','March','April','May','June','July','August','September','October','November','December'][bsMonth - 1];
  const projectHasRetainer = !!bsSelProj?.hasRetainer;
  // Balance FORWARD excludes this summary's own draw (2026-08-05): the
  // send writes the draw, so a post-send render would otherwise show the
  // pre-draw balance already netted and then subtract the subtotal again
  // (Cimon's -$28,478.66 preview). The archived as-sent cover was always
  // generated pre-draw and is unaffected; this makes the LIVE workbench
  // agree with it before and after sending.
  const ledgerForForward = bsSavedRecord
    ? (retainerLedger || []).filter(en => !(en && en.sourceType === 'billing_summary' && String(en.sourceId || '') === String(bsSavedRecord.id)))
    : retainerLedger;
  const ledgerBalance      = bsSelProj ? calcRetainerBalance(ledgerForForward, bsSelProj.label) : 0;
  const bsDesignAmt       = parseFloat(bsDesignAmount) || 0;
  const bsAddlTotal       = bsAdditionalServices.reduce((s, svc) => s + (parseFloat(svc.amount) || 0), 0);
  const bsExpTotal        = bsExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  // ── Reimbursable receipt picker (since 2026-05-18) ──────────────────────
  //
  // Replaces the previous reimbursable-invoice flow. Approved
  // project_reimbursable receipts for this project, untagged onto any
  // billing summary, are offered as line items the bookkeeper can tick
  // to include on this summary.
  //
  // No period window — receipts from any date are eligible until they're
  // tagged. Eliminates the period-window filter bug class that the old
  // invoice-driven model carried (created_at.startsWith(periodPrefix)).
  //
  // "Sticky" pattern: a receipt the bookkeeper has already ticked onto
  // THIS draft (its id is in bsReimbursableSelections) stays in the
  // picker even after persistRecord('sent') writes its billed_on_summary_id
  // — so the bookkeeper can see what's on this summary at any point.
  // The untagged-or-sticky predicate mirrors the proposals/invoices
  // picker convention elsewhere in this file.
  const bsReimbursableReceiptsAll = useMemo(() => {
    if (!bsSelProj) return [];
    var currentSummaryId = bsSavedRecord?.id || null;
    return (receipts || [])
      .filter(function(r) { return r && r.kind === 'project_reimbursable'; })
      .filter(function(r) { return r.status === 'approved' || r.status === 'pushed'; })
      // Absorbed receipts (2026-08-23) never bill — the firm ate the cost.
      .filter(function(r) { return !receiptIsAbsorbed(r); })
      .filter(function(r) { return r.projectLabel === bsSelProj.label; })
      .filter(function(r) {
        var tag = r.billedOnSummaryId || r.billed_on_summary_id || '';
        if (!tag) return true;
        if (currentSummaryId && tag === currentSummaryId) return true;
        return bsReimbursableSelections.has(r.id);
      })
      .slice()
      .sort(function(a, b) {
        return String(a.receiptDate || '').localeCompare(String(b.receiptDate || ''));
      });
  }, [receipts, bsSelProj, bsSavedRecord, bsReimbursableSelections]);

  // Marked-up sum of currently-selected receipts. Looked up against the
  // LIVE receipts state so edits propagate. Feeds bsSubtotal via line ~405
  // and is what calcBillingSummaryAmount sums in lockstep — the two must
  // agree to the cent. Guard test: calcBillingSummaryAmountReceipts.test.js.
  const bsReimbursableTotal = useMemo(() => {
    var lines = buildSummaryReceiptLines(
      Array.from(bsReimbursableSelections),
      receipts
    );
    return Math.round(
      lines.reduce(function(s, ln) { return s + ln.amount; }, 0) * 100
    ) / 100;
  }, [bsReimbursableSelections, receipts]);

  // Retainer projects fold reimbursables into the asking amount —
  // the cover renders them in the retainer-eligible section above
  // SUBTOTAL alongside Design Services and Additional Services. The
  // calcBillingSummaryTotalDue helper contract is unchanged; the
  // "subtotal includes reimbursables for retainer projects" rule is
  // enforced here, upstream of the helper, so the helper's existing
  // tests stay valid.
  // Picked receipts count for BOTH billing models (2026-07-01): they
  // render as PROJECT EXPENSES rows on the cover, so their dollars must
  // be inside the services ask — retainer projects absorb them via the
  // draw; non-retainer clients are billed directly. Pre-fix the
  // non-retainer path showed the rows but excluded them from every
  // total (never billed anywhere).
  const bsSubtotal        = bsDesignAmt + bsAddlTotal + bsExpTotal + bsReimbursableTotal;
  const bsCurrentRetainer = ledgerBalance;
  const bsNewRetainer     = bsCurrentRetainer - bsSubtotal;
  // Per-project retainer threshold (since 2026-05-11). Negotiated per
  // contract with each client. Configured in Project Directory → Billing.
  // Falls back to RETAINER_THRESH_DEFAULT for legacy projects without
  // the field set yet (the on-enable handler in adminTabs sets it to
  // the default at first checkbox toggle).
  const RETAINER_THRESH_DEFAULT = 60000;
  // Threshold 0 is a REAL setting — "burn the retainer down, never ask
  // for a top-up again" (Cimon 2026-08-05, ending Hoback's retainer).
  // Pre-fix `parseFloat(...) || DEFAULT` treated 0 as falsy and silently
  // asked for $60,000 anyway.
  const bsParsedThreshold = bsSelProj ? parseFloat(bsSelProj.retainerThreshold) : NaN;
  const bsRetainerThreshold = Number.isFinite(bsParsedThreshold) ? bsParsedThreshold : RETAINER_THRESH_DEFAULT;
  // Auto-computed default retainer request: when post-period balance
  // falls below the project's threshold, request a top-up back to it.
  // Otherwise zero (no request line). Bookkeeper can override.
  // A draw beyond the remaining balance FLOWS TO AMOUNT DUE (Cimon
  // 2026-08-05: "if there's ever a negative balance that needs to flow
  // to amount due"): the auto-request is never less than the shortfall,
  // so the client is asked for exactly the overage, their payment lands
  // as the closing retainer deposit, and the balance ends at $0 — a
  // negative balance never reaches a client statement. With threshold 0
  // (retainer wind-down) the shortfall is the ONLY thing ever requested.
  const bsShortfall = Math.max(0, Math.round(-bsNewRetainer * 100) / 100);
  const bsThresholdRequest = (projectHasRetainer && bsNewRetainer < bsRetainerThreshold) ? bsRetainerThreshold : 0;
  const bsAutoRetainerRequest = projectHasRetainer ? Math.max(bsThresholdRequest, bsShortfall) : 0;
  // Effective request — override wins, else auto-computed.
  const bsRetainerRequest = bsRetainerRequestOverride != null ? bsRetainerRequestOverride : bsAutoRetainerRequest;
  // Total Due — math lives in utils.calcBillingSummaryTotalDue. See the
  // helper's docstring for the why. Retainer projects: Total Due is the
  // Retainer Request only (the Subtotal is paid out of the retainer
  // balance, not billed on top). Non-retainer projects: Total Due equals
  // the Subtotal. Guard test: __tests__/billingSummaryRetainer.test.jsx.
  const bsTotalDue = calcBillingSummaryTotalDue({
    projectHasRetainer: projectHasRetainer,
    subtotal: bsSubtotal,
    retainerRequest: bsRetainerRequest,
  });

  // Auto-include filter (rewritten 2026-05-11 alongside two-path Mark Sent).
  //
  //   status === 'Sent'                -- only delivered proposals
  //   sentVia === 'billing_summary'    -- off-cycle path is excluded by design
  //   !billedOnSummaryId               -- not already attached elsewhere
  //     OR  billedOnSummaryId === currentSummaryId  -- sticky on this draft
  //
  // No month/year filter. Proposals stay in the pending bundle until the
  // bookkeeper sends the summary (which tags them) or excludes them
  // explicitly. Once tagged onto a summary they only appear on THAT
  // summary, never re-pulled into a future one.
  //
  // Approved proposals fall out of this filter (status flipped past Sent
  // when Client Approved fired). Historical Approved rows from before the
  // schema migration carry sentVia=null and were intentionally not
  // backfilled — they predate the two-path system and the manual Excel
  // billing summaries are the system of record for that period. See
  // FOLLOWUPS.md for the historical-mislabel gap.
  const bsProposalsList = useMemo(() => {
    if (!bsSelProj) return [];
    const projectLabel = bsSelProj.label;
    const currentSummaryId = bsSavedRecord?.id || null;
    return proposals
      .filter(p => p.project === projectLabel)
      .filter(p => p.status === 'Sent' && p.sentVia === 'billing_summary')
      // Defensive: off-cycle proposals are delivered standalone and do
      // not belong on a billing summary. The prior filter already
      // implicitly excludes them (sentVia must equal 'billing_summary'),
      // but this line is the explicit guard — if a future change
      // broadens the prior filter (e.g. to include Approved proposals
      // or to drop the sentVia check), off-cycle would still be
      // excluded here. Belt-and-suspenders against future filter
      // loosening. Per commit 0dec277 (two-path Mark Sent), off-cycle
      // proposals have their own delivery channel.
      .filter(p => p.sentVia !== 'off_cycle')
      .filter(p => !p.billedOnSummaryId || p.billedOnSummaryId === currentSummaryId)
      .filter(p => !bsProposalsExcluded[p.id])
      .map(p => {
        const overrideDate = bsProposalDateOverrides[p.id];
        const baseDate = overrideDate || fmtSentDate(p.sent_at) || fmtSentDate(p.created_at) || '';
        // Compute totals LIVE from current item state instead of reading
        // the stored p.total_client / p.total_deposit fields. The stored
        // fields go stale when items are edited after the proposal was
        // last saved (e.g. price tweak, peer added/removed) -- the cover
        // PDF and history would otherwise show numbers from before the
        // edit.
        const liveItems = (p.items || [])
          .map(id => allItems.find(it => it.id === id))
          .filter(Boolean);
        // totalClient is the FULL contract value — products + every
        // marked-up fee bucket. Same definition the proposal PDF's
        // "Proposal Total" line uses. Previously this was products-only
        // (assemblyClient sum), which made the billing summary's TOTAL
        // column disagree with the proposal PDF for in-stock proposals
        // where fees collect on the deposit invoice.
        const liveClient  = proposalGrandTotal(liveItems, allItems, bsSelProj);
        // Fix C Phase 4 (2026-05-27): pass bsSelProj into itemDepositAmount
        // so the cdp=100 deposit basis (vendor-100% items where fees ride
        // deposit) uses the FRESH project-derived sales tax instead of the
        // stored item.fees.salesTax. Without this, the BS procurement
        // total reads stale stored values even after a project's tax_rate
        // is configured — defeating the Phase-1 derivation upstream.
        // 2026-07-08 — canonical deposit-ask formula: itemDepositAmount
        // (project passed for fresh tax) × ccFeeMul, ROUNDED PER ITEM.
        // This is byte-identical to how buildDepositInvoicePayload computes
        // each per-item DEP's amount_due at approval time, so the number
        // the client sees on the cover equals the sum of the DEP invoices
        // minted later — Billing History's ask no longer shifts when its
        // source switches from the frozen cover to the DEP rows. Pre-fix
        // this reduce skipped ccFeeMul (DEP applied it) and rounded only
        // the total (DEPs round per item).
        const liveDeposit = Math.round(liveItems.reduce((s, it) =>
          s + Math.round(itemDepositAmount(it, allItems, bsSelProj) * ccFeeMul(it) * 100) / 100, 0) * 100) / 100;

        // Find the deposit invoice tied to this proposal — used to
        // determine outstanding amount for the alert.
        const depInvoice = invoices.find(inv =>
          (inv.invoiceType === 'deposit') &&
          (inv.proposalId === p.id || inv.proposal_id === p.id)
        );
        const amountPaid   = depInvoice
          ? calcPaidForDocument(paymentAllocations, 'invoice', depInvoice.id)
          : 0;
        const isPaid       = depInvoice && depInvoice.status === 'paid';
        const isOutstanding = liveDeposit > 0 && !isPaid;
        return {
          proposal:      p,
          displayDate:   baseDate,
          totalDeposit:  liveDeposit,
          totalClient:   liveClient,
          isOutstanding: isOutstanding,
          amountOwed:    isOutstanding ? Math.max(liveDeposit - amountPaid, 0) : 0,
        };
      })
      // Sort chronological (oldest → newest). Tie-breaker on proposal-ref
      // sequence — when multiple proposals share a sent_at date (typical
      // for a monthly batch), displayDate strings are byte-identical and
      // the primary sort is a no-op. Without the tie-breaker the
      // underlying array order wins, and AppContext loads proposals with
      // `.order('created_at', {ascending: false})` — so same-day batches
      // arrived reverse-chronological (P-011, P-010, P-009 instead of
      // P-009, P-010, P-011). Ref-seq tie-breaker locks in stable
      // chronological order across all downstream consumers (PDF payload,
      // zip bundling, manifest count, form UI table).
      .sort((a, b) => {
        const dateCmp = a.displayDate.localeCompare(b.displayDate);
        if (dateCmp !== 0) return dateCmp;
        const refSeq = ref => {
          const m = String(ref || '').match(/-P-(\d+)$/i);
          return m ? parseInt(m[1], 10) : 0;
        };
        return refSeq(a.proposal.proposal_ref) - refSeq(b.proposal.proposal_ref);
      });
  }, [proposals, invoices, paymentAllocations, bsProjId, bsProposalDateOverrides, bsProposalsExcluded, bsSelProj, bsSavedRecord, allItems]);

  const bsProposalsTotal     = bsProposalsList.reduce((s, r) => s + r.totalClient, 0);
  const bsProposalsDeposit   = bsProposalsList.reduce((s, r) => s + r.totalDeposit, 0);
  const bsProposalsOutstanding = bsProposalsList.reduce((s, r) => s + r.amountOwed, 0);

  // Auto-include filter for invoices (rewritten 2026-05-11). Mirrors the
  // proposal filter's shape but without sent_via — invoices have no
  // delivery-path concept (they're always tied to a specific summary if
  // anywhere). No month/year filter; invoices stay pending until the
  // bookkeeper sends the summary or excludes them explicitly.
  //
  //   project matches
  //   status not in {void, cancelled}   -- defensive; non-billable
  //   !billedOnSummaryId                -- not attached elsewhere
  //     OR  billedOnSummaryId === currentSummaryId  -- sticky on this draft
  const bsInvoicesList = useMemo(() => {
    if (!bsSelProj) return [];
    const projectLabel = bsSelProj.label;
    const currentSummaryId = bsSavedRecord?.id || null;

    return invoices
      .filter(inv => (inv.project || inv.projectLabel || inv.project_label) === projectLabel)
      .filter(inv => {
        // Billing summaries are forward-looking — they request payment
        // for things being settled in this summary. An invoice that is
        // already 'paid' is done; showing it on a new summary's cover
        // suggests something is owed when nothing is. void/cancelled
        // are non-billable; same reasoning.
        //
        // 2026-06-26 (FIN restructure Commit 1): 'draft' status is now
        // ALSO excluded. The new lifecycle contract — locked alongside
        // the planned Open-tab removal — is that a FIN is bundled on a
        // summary ONLY after the bookkeeper explicitly clicks Mark
        // Ready (status='draft' → 'ready'). Draft FINs are work in
        // progress; admitting them caused auto-leaks onto whatever
        // summary happened to be open next. Off-cycle deliveries (the
        // per-FIN Mark Sent button) still bypass this filter — their
        // status flips straight to 'sent' without ever being 'ready'.
        //
        // Operator note for the deploy of this commit: any in-flight
        // draft FIN the bookkeeper expects on the next summary must
        // be promoted to 'ready' via the Mark Ready button. One-time
        // migration action.
        var st = String(inv.status || '').toLowerCase();
        return st !== 'void' && st !== 'cancelled' && st !== 'paid' && st !== 'draft';
      })
      // 2026-10-07 (Cimon): DEPs are internal-only system records. The
      // client never receives one; the proposal is the collection
      // vehicle at deposit stage. A DEP must never ride a billing
      // summary regardless of status. The Theodore Ellison glass DEP
      // had been marked 'ready', which moved it past the draft filter
      // above and made it reappear on every monthly summary until
      // excluded by hand. Only FINs (and reimbursables on non-retainer
      // projects, handled below) belong in this list.
      .filter(inv => (inv.invoiceType || inv.invoice_type) !== 'deposit')
      // For retainer projects, reimbursables are rendered inline in the
      // retainer-eligible section above SUBTOTAL — they must NOT also
      // appear in the bottom Invoice Activity table (would double-count
      // on screen and in the cover PDF). For non-retainer projects,
      // reimbursables stay in this list and render at the bottom as
      // before.
      .filter(inv => !(projectHasRetainer && (inv.invoiceType || inv.invoice_type) === 'reimbursable'))
      .filter(inv => !inv.billedOnSummaryId || inv.billedOnSummaryId === currentSummaryId)
      .filter(inv => !bsInvoicesExcluded[inv.id])
      .map(inv => {
        // Rule 10 (2026-07-18): the row's AMOUNT is the OUTSTANDING
        // balance — full ask minus cash actually allocated — never the
        // stored ask alone. A partially paid deposit invoice (the
        // Kumiko FX case: $115,198.47 recorded against a $119,823.26
        // ask) shows the client the $4,624.79 still owed, not a
        // full-price re-ask of money already in the bank.
        const fullAsk = parseFloat(inv.amountDue ?? inv.amount_due ?? inv.amount ?? 0) || 0;
        const paid = Math.round(calcPaidForDocument(paymentAllocations, 'invoice', inv.id) * 100) / 100;
        // Statement presentation (2026-09-07, Cimon): a merchandise FIN row
        // prints the invoice's CURRENT AMOUNT DUE and nothing else. The
        // deposit is already netted inside `amount` (subtotal − amountDue is
        // the Fix E identity; allocated cash comes off on top), so there is
        // no second deduction line and no gross figure on the statement.
        // Shipping the gross row plus a LESS: DEPOSIT PREVIOUSLY PAID
        // section earlier the same day was reverted — same total, but the
        // client had to do arithmetic to find what they owe.
        return {
          invoice: inv,
          displayDate: String(inv.sentAt || inv.sent_at || inv.createdAt || inv.created_at || '').slice(0, 10),
          amount: Math.max(0, Math.round((fullAsk - paid) * 100) / 100),
          fullAsk: fullAsk,
          paid: paid,
        };
      })
      // Settled rows ask nothing — drop them. This is what finally lets
      // $0-due FINs (100%-upfront orders whose deposit collected
      // everything) close out: they can never become status='paid'
      // because there is no payment left to allocate, so the status
      // filter above never catches them. Allocations are the authority
      // (Rule 10); an invoice with no outstanding balance has no
      // business on a forward-looking ask document.
      .filter(row => row.amount > 0.005)
      .sort((a, b) => a.displayDate.localeCompare(b.displayDate));
  }, [invoices, paymentAllocations, bsSelProj, bsInvoicesExcluded, bsSavedRecord, projectHasRetainer]);

  const bsInvoicesTotal = bsInvoicesList.reduce((s, r) => s + r.amount, 0);

  // (The Record Client Payment wizard — its allocatable-targets memo, the
  // wizard open/PDF state, and the open handler — moved to Billing
  // History's package preview on 2026-07-02. Cimon: "Billing history
  // should be the place to record payment"; the bottom-of-workbench
  // placement behind post-send edit guards was confusing. The target
  // derivation lives on as the pure buildPackagePaymentTargets in
  // utils.js, which preserves the per-item-DEP filter-never-find
  // invariant this file used to pin. This workbench is build/send only.)

  // Draft-FIN warning modal: null, or the array of draft FINs that would
  // be left off the package (populated by sendPackage's visibility gate).
  const [bsDraftFinWarn, setBsDraftFinWarn] = useState(null);

  // Reopen-as-draft confirm (2026-07-20): open flag + busy state for the
  // sent-summary revision path. See reopenAsDraft() for the contract.
  const [bsReopenConfirm, setBsReopenConfirm] = useState(false);
  // Send confirmation (2026-07-27). Both official-send affordances were
  // one-click with no modal — the July 18 incident: clicking Send Package
  // to LOOK at the zip (pre-Preview-ZIP, it was the only way) fired the
  // real send: sent_at, retainer draw, tagging, archives. Client-affecting
  // transitions route through a confirmation (CLAUDE.md modal pattern).
  // null | 'package' | 'mark_sent'
  const [bsSendConfirm, setBsSendConfirm] = useState(null);
  const [bsReopenBusy, setBsReopenBusy] = useState(false);

  // ── Available client credits (picker) ──────────────────────────────────
  //
  // Source-of-truth credit entries for the current project's client. Each
  // returned row carries the live `available` balance (original.amount +
  // SUM(applied where source_credit_id = original.id)). The picker offers
  // these for application to the current billing summary's procurement
  // section. Excludes applications tied to the CURRENT summary so its
  // own contribution doesn't double-count against itself (excludeSummaryId
  // option on calcCreditAvailableBalance).
  //
  // Original credit entries are the types that ADD to client balance —
  // CLIENT_CREDIT_SOURCE_TYPES (cancelled_deposit / co_credit /
  // overpayment; the pair was hardcoded here until 2026-09-04, which
  // hid every payment-wizard overpayment credit from this picker).
  // Applied entries (the negative rows that draw the balance down) are
  // filtered out.
  const bsAvailableCreditsList = useMemo(function() {
    if (!bsSelProj || !bsClient) return [];
    var clientId = bsClient.id;
    var summaryId = bsSavedRecord?.id || '';
    return (clientCreditLedger || [])
      .filter(function(e) {
        if (!e) return false;
        if (String(e.clientId || '') !== String(clientId)) return false;
        return CLIENT_CREDIT_SOURCE_TYPES.indexOf(e.entryType) >= 0;
      })
      .map(function(e) {
        var available = calcCreditAvailableBalance(e, clientCreditLedger, { excludeSummaryId: summaryId });
        return Object.assign({}, e, { available: available });
      })
      // Fully-consumed credits leave the picker (2026-09-04, Cimon:
      // "once a credit is zero it shouldn't appear on billing summary").
      // The one exception: a credit applied on THIS draft always stays
      // visible — hiding it while its dollars still count in the
      // applied total would be invisible money.
      .filter(function(e) {
        return e.available > 0.005 || !!bsAppliedCredits[String(e.id)];
      })
      .sort(function(a, b) {
        return String(b.entryDate || '').localeCompare(String(a.entryDate || ''));
      });
  }, [clientCreditLedger, bsClient, bsSelProj, bsSavedRecord, bsAppliedCredits]);

  // Sum of currently-applied credit amounts on this draft. Drives the
  // procurement amount-due clamp and the PDF CREDITS row group footer.
  const bsAppliedCreditsTotal = useMemo(function() {
    var total = Object.keys(bsAppliedCredits).reduce(function(s, k) {
      return s + (parseFloat(bsAppliedCredits[k].amount) || 0);
    }, 0);
    return Math.round(total * 100) / 100;
  }, [bsAppliedCredits]);

  // Procurement amount due BEFORE credits — outstanding deposits across
  // pending proposals + invoice activity total. This is the number the
  // PDF's procurement AMOUNT DUE line shows when no credits are applied.
  const bsProcAmountDueGross = Math.round((bsProposalsOutstanding + bsInvoicesTotal) * 100) / 100;

  // Clamped at $0 per SPEC decision 2026-05-22. Negative would mean the
  // applied credits exceed the procurement ask; the cover renders $0 with
  // an informational note showing the excess remaining for future billing.
  const bsProcAmountDueNet = Math.max(0, Math.round((bsProcAmountDueGross - bsAppliedCreditsTotal) * 100) / 100);
  const bsCreditsExcess = Math.max(0, Math.round((bsAppliedCreditsTotal - bsProcAmountDueGross) * 100) / 100);

  // Live cover preview — debounced server regeneration into the right pane.
  // Fires 1.5s after any form change once a project is selected. Read-only:
  // does not persist anything, just renders a fresh PDF blob into the iframe.
  useEffect(() => {
    if (!bsProjId) {
      setBsLivePdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
      return;
    }
    // Settled-state gates (2026-08-15 — the "July hours on August"
    // phantom): flipping the period changes bsMonth INSTANTLY while the
    // hours lookup + saved-record load resolve asynchronously. A render
    // armed inside that window snapshots MIXED state — the new month's
    // label with the old month's hours — and if the lookup is slow the
    // mixed frame completes and sits in the pane looking like data
    // corruption. Never render until this period's record load has
    // published its key and no hours lookup is in flight; both gates
    // are deps, so the effect re-arms the moment loading settles.
    if (bsHoursLoading) return;
    if (bsRecordLoadedKey !== bsProjId + '|' + bsYear + '|' + bsMonth) return;
    let cancelled = false;
    // Abort hardening (2026-07-18): a hung request must never pin the
    // pane on "Generating preview…" — the controller kills the fetch
    // when the effect re-arms/unmounts, and a hard 60s timeout turns a
    // stalled connection into a visible, retryable error instead of an
    // eternal spinner.
    const ctrl = new AbortController();
    const handle = setTimeout(async () => {
      setBsLivePdfLoading(true);
      setBsLivePdfError('');
      const kill = setTimeout(() => ctrl.abort(), 60000);
      try {
        const payload = buildBsPayload();
        const res = await fetch('/api/generate-billing-summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
        const result = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(result.error || 'API error');
        const bytes = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
        const blob  = new Blob([bytes], { type: 'application/pdf' });
        const url   = URL.createObjectURL(blob);
        setBsLivePdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
        setBsLivePdfPeriod(payload.forPeriod || '');
      } catch (e) {
        if (!cancelled) {
          setBsLivePdfError(e && e.name === 'AbortError'
            ? 'Preview timed out — check the connection, then edit any field to retry.'
            : e.message);
        }
      } finally {
        clearTimeout(kill);
        if (!cancelled) setBsLivePdfLoading(false);
      }
    }, 1500);
    return () => { cancelled = true; clearTimeout(handle); ctrl.abort(); };
    // Dep sweep (2026-07-20): bsInvoicesList (invoice ×-exclusions +
    // allocation-driven outstanding amounts), bsReimbursableSelections
    // (receipt picker ticks), and bsHoursInvoice (attach/refresh) all
    // change the rendered cover but were missing here — edits to any of
    // them left the preview stale until an unrelated field changed.
    // Safe as deps since 10539b1: the list memos are identity-stable
    // (bsSavedRecord no longer churns), so this cannot re-open the
    // autosave↔preview loop.
  }, [bsProjId, bsMonth, bsYear, bsDesignHours, bsDesignAmount, bsAdditionalServices,
      bsExpenses, bsRetainerRequestOverride, bsHoursNarratives, bsProposalsList,
      bsInvoicesList, bsReimbursableSelections, bsHoursInvoice, bsExpensesDisplay,
      bsClient, projectHasRetainer, bsCurrentRetainer, bsAppliedCredits,
      bsHoursLoading, bsRecordLoadedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave draft (2026-06-28 — Cimon: 'I need to be able to add
  // things to the billing summary draft as I go.' Prior model
  // required explicit Save Draft clicks; anything not clicked was
  // lost on page reload, and the over-aggressive save filter
  // dropped partial entries even when she DID click Save Draft).
  // Same 1.5s debounce as the live PDF preview. Skips when no
  // project is picked, when the saved record has been sent or
  // paid (terminal — never auto-overwrite), and when the draft-
  // conflict gate is active (the existing draft is for a
  // different period). Persist failures log to console; the
  // bookkeeper still has Save Draft for an explicit re-try.
  useEffect(() => {
    if (!bsProjId || !bsSelProj) return;
    // Never persist before this period's saved-record load resolves
    // (2026-07-27): pre-load, bsSavedRecord is null even when a SENT
    // summary exists, and the draft write would downgrade it.
    if (bsRecordLoadedKey !== bsProjId + '|' + bsYear + '|' + bsMonth) return;
    if (bsSavedRecord && (bsSavedRecord.status === 'sent' || bsSavedRecord.status === 'paid')) return;
    if (bsDraftConflict) return;
    const handle = setTimeout(() => {
      persistRecord('draft').catch(e => console.error('[bs-autosave]', e));
    }, 1500);
    return () => clearTimeout(handle);
    // bsSavedRecord in the deps is load-bearing (2026-07-27): when a send
    // flips the record to sent, the effect re-runs and CANCELS any timer
    // armed by a pre-send edit — otherwise that stale timer fires
    // persistRecord('draft') after the send and flips the just-sent
    // summary back to draft. Identity-stable since 10539b1, so this
    // cannot re-open the autosave loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bsProjId, bsMonth, bsYear, bsDesignHours, bsDesignAmount, bsAdditionalServices,
      bsExpenses, bsRetainerRequestOverride, bsHoursNarratives, bsProposalsList,
      bsClient, projectHasRetainer, bsCurrentRetainer, bsAppliedCredits,
      bsReimbursableSelections, bsExpensesDisplay, bsSavedRecord, bsRecordLoadedKey]);

  // ── ACTIONS ──

  async function persistRecord(status) {
    if (!bsSelProj) return null;

    // Terminal-status protection (2026-07-27): a 'draft' persist must
    // NEVER land on a sent/paid row — it would silently reopen a summary
    // the client already received and overwrite its snapshot columns.
    // The ref (not the render closure) catches a stale debounce timer
    // that armed before a send completed; the DB check further down
    // catches the load race where the row exists but this session hasn't
    // loaded it yet. The only sanctioned sent→draft path is the explicit
    // "Reopen as draft…" confirmation flow.
    if (status === 'draft') {
      const live = bsSavedRecordRef.current;
      if (live && (live.status === 'sent' || live.status === 'paid')) {
        console.warn('[bs-persist] refused a draft write over the ' + live.status + ' summary ' + live.id);
        return null;
      }
    }

    // Resolve the record id BEFORE upserting. The unique constraint is on
    // (project_label, year, month), so an upsert with a fresh UUID against
    // a row that already exists in another session would overwrite the
    // existing id and orphan every record tagged via billed_on_summary_id.
    // Look up first; only mint a new UUID when no row exists.
    let recordId = bsSavedRecord?.id || null;
    if (!recordId) {
      const { data: existing } = await supabase.from('billing_summaries')
        .select('id, status')
        .eq('project_label', bsSelProj.label)
        .eq('year', bsYear)
        .eq('month', bsMonth)
        // monthly only — an off_cycle row for the same period must never
        // be resolved (and refused/overwritten) as "the" monthly. Also
        // keeps maybeSingle() from throwing when two off-cycles exist.
        .eq('summary_type', 'monthly')
        .maybeSingle();
      if (existing?.id) {
        if (status === 'draft' && (existing.status === 'sent' || existing.status === 'paid')) {
          console.warn('[bs-persist] refused a draft write over the ' + existing.status + ' summary ' + existing.id + ' (record not yet loaded in this session)');
          return null;
        }
        recordId = existing.id;
      } else {
        recordId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : Date.now() + '-' + Math.random();
      }
    }
    const row = {
      id: recordId,
      project_label:        bsSelProj.label,
      client_name:          bsClient?.name || '',
      year:                 bsYear,
      month:                bsMonth,
      status:               status,
      has_retainer:         projectHasRetainer,
      design_hours:         parseFloat(bsDesignHours) || 0,
      design_amount:        bsDesignAmt,
      // Save partial entries (2026-06-28 — Cimon: 'save for a while
      // then lost'). Pre-fix, filter dropped any row missing either
      // half (e.g. name typed, amount not yet) so half-completed work
      // silently vanished on next page load. Now save anything with
      // SOMETHING in it; the renderer skips fully-empty rows at
      // display time. Filter still strips fully-empty rows so the
      // DB doesn't accumulate {'name':'','amount':''} noise.
      additional_services:  bsAdditionalServices.filter(s => (s.name || s.amount)),
      expenses:             bsExpenses.filter(e => (e.description || e.amount)),
      procurement:          {
        proposalTotal:    bsProposalsTotal,
        depositRequested: bsProposalsDeposit,
        invoicesDue:      bsProposalsOutstanding,
      },
      proposals_this_month: bsProposalsList.map(row => ({
        id: row.proposal.id,
        ref: row.proposal.proposal_ref || '',
        name: row.proposal.proposal_name || '',
        sent_date: row.displayDate,
        total_deposit: row.totalDeposit,
        total_client: row.totalClient,
        is_outstanding: row.isOutstanding,
        amount_owed: row.amountOwed,
      })),
      invoices_this_month: bsInvoicesList.map(row => ({
        id: row.invoice.id,
        ref: row.invoice.invoiceRef || row.invoice.invoice_ref || '',
        type: row.invoice.invoiceType || row.invoice.invoice_type || '',
        sent_date: row.displayDate,
        amount: row.amount,
      })),
      hours_narratives:     bsHoursNarratives,
      retainer_balance_before: bsCurrentRetainer,
      retainer_balance_after:  projectHasRetainer ? bsNewRetainer : 0,
      retainer_request:     projectHasRetainer ? bsRetainerRequest : null,
      hours_invoice_number: bsHoursInvoice?.invoice_number || '',
      // Reimbursable-receipt selection state. IDs only — descriptions
      // and amounts re-derive from the live receipts table on render.
      selected_receipt_ids: Array.from(bsReimbursableSelections),
      created_by:           userEmail,
    };
    if (status === 'sent' && !bsSavedRecord?.sent_at) row.sent_at = new Date().toISOString();
    // Statement expense presentation (2026-08-23). The key is written only
    // when the feature is in use (or already stored on this row), so saves
    // keep working before the operator runs 20260823_billing_presentation.sql
    // — this upsert has no unknown-column stripping, and an always-present
    // key would fail every save until the column exists.
    var bsHasGroups = (bsExpensesDisplay.groups || []).length > 0;
    if (bsExpensesDisplay.consolidate || bsHasGroups
        || (bsSavedRecord && bsSavedRecord.expenses_display != null)) {
      row.expenses_display = (bsExpensesDisplay.consolidate || bsHasGroups)
        ? { consolidate: !!bsExpensesDisplay.consolidate, label: bsExpensesDisplay.label || '', groups: bsExpensesDisplay.groups || [] }
        : null;
    }
    const { data, error } = await supabase.from('billing_summaries')
      .upsert(row, { onConflict: 'id' })
      .select()
      .single();
    if (error) {
      // Friendlier message for the partial-unique-index collision.
      if (String(error.message || '').includes('billing_summaries_one_draft_per_project_idx')
          || error.code === '23505') {
        setBsSummaryError('Another draft already exists for this project. Open it from "Continue with Summary" first, or send/delete it before starting a new one.');
      } else if (String(error.message || '').includes('expenses_display')) {
        setBsSummaryError('The consolidated-expenses option needs a database migration: run supabase/migrations/20260823_billing_presentation.sql in the Supabase Dashboard, then save again (or untick "Consolidate expenses" to save without it).');
      } else {
        setBsSummaryError(error.message);
      }
      return null;
    }
    // Identity-stable update (2026-07-18): every autosave used to swap in
    // a fresh row object here; bsProposalsList/bsInvoicesList memos dep on
    // bsSavedRecord, so their identities churned, and the autosave +
    // live-preview effects (which dep on those lists) re-armed — an
    // infinite 1.5s save/regenerate loop that pinned the preview on
    // "Generating preview…" forever. Consumers only read id / status /
    // sent_at; when those match, keep the previous object so the memo
    // chain stays quiet.
    setBsSavedRecord(prev => {
      if (prev && data && prev.id === data.id && prev.status === data.status
          && (prev.sent_at || null) === (data.sent_at || null)) {
        // Mirror expenses_display onto the KEPT object (2026-08-30,
        // review finding): the write-gate above reads
        // bsSavedRecord.expenses_display to decide whether the key must
        // be written. Keeping a stale null here meant: add a group →
        // autosave persists it → delete the group → gate sees stale null
        // on all sides → key omitted from the upsert → the DB keeps the
        // deleted group, which resurrects on reload and prints on the
        // client statement. Mutating the kept object preserves identity
        // (the whole point of this branch) while keeping the gate honest.
        if ((prev.expenses_display || null) !== (data.expenses_display || null)) {
          prev.expenses_display = data.expenses_display != null ? data.expenses_display : null;
        }
        return prev;
      }
      return data;
    });
    setBsLastSavedAt(new Date());

    // ── Persist applied-credit ledger rows ──────────────────────────────
    //
    // Diff bsAppliedCredits against the applied_to_billing_summary rows
    // already in clientCreditLedger for this summary id.
    //   - In current map, no ledger row yet  → INSERT
    //   - In current map, ledger row exists, amount differs → UPDATE
    //   - Not in current map, ledger row exists for this summary → DELETE
    //
    // Writes happen on every persist (draft + sent) — the FK ON DELETE
    // CASCADE on target_summary_id guarantees the rows disappear if the
    // summary is later deleted, restoring the available balance on the
    // original credit. Stored amounts are NEGATIVE so the per-credit
    // balance compute (original.amount + SUM(applied)) works out.
    const summaryId = data.id;
    const existingApplied = (clientCreditLedger || []).filter(function(e) {
      return e && e.entryType === 'applied_to_billing_summary'
          && String(e.targetSummaryId || '') === String(summaryId);
    });
    const existingBySourceId = {};
    existingApplied.forEach(function(e) {
      if (e.sourceCreditId) existingBySourceId[String(e.sourceCreditId)] = e;
    });
    const newAppliedState = Object.assign({}, bsAppliedCredits);
    const ledgerInserts = [];
    const ledgerUpdates = [];
    const ledgerDeletes = [];

    Object.keys(bsAppliedCredits).forEach(function(sourceCreditId) {
      const sel = bsAppliedCredits[sourceCreditId];
      const amt = parseFloat(sel.amount) || 0;
      if (amt <= 0) return;
      const existing = existingBySourceId[sourceCreditId];
      const source = (clientCreditLedger || []).find(function(e) { return e && String(e.id) === String(sourceCreditId); });
      if (!source) return;
      // Statement wording for this application, stored in `notes`.
      const lbl = String(sel.label || '').trim() || creditPrintLabel(source.entryType);
      if (!existing) {
        ledgerInserts.push({
          sourceCreditId: sourceCreditId,
          source: source,
          amount: amt,
          label: lbl,
        });
      } else if (Math.abs(Math.abs(parseFloat(existing.amount) || 0) - amt) > 0.005
                 || String(existing.notes || '').trim() !== lbl) {
        ledgerUpdates.push({
          id: existing.id,
          amount: amt,
          label: lbl,
        });
      }
    });
    existingApplied.forEach(function(e) {
      const sourceId = String(e.sourceCreditId || '');
      if (!bsAppliedCredits[sourceId]) ledgerDeletes.push(e.id);
    });

    let creditWriteError = null;

    if (ledgerInserts.length > 0) {
      const insertRows = ledgerInserts.map(function(ins) {
        const row = clientCreditToRow({
          clientId:       ins.source.clientId,
          entryDate:      new Date().toISOString().slice(0, 10),
          entryType:      'applied_to_billing_summary',
          amount:         -ins.amount,
          sourceCreditId: ins.sourceCreditId,
          targetSummaryId: summaryId,
          reference:      'Applied to ' + (bsSelProj?.label || '') + ' ' + bsMonthName + ' ' + bsYear,
          // `notes` carries the statement wording (see bsAppliedCredits).
          notes:          ins.label || '',
          createdBy:      userEmail,
        });
        delete row.id;
        return row;
      });
      const { data: inserted, error: insErr } = await supabase
        .from('client_credit_ledger')
        .insert(insertRows)
        .select();
      if (insErr) {
        creditWriteError = insErr.message;
      } else if (inserted) {
        inserted.forEach(function(r, idx) {
          const mapped = rowToClientCredit(r);
          const sourceId = ledgerInserts[idx].sourceCreditId;
          newAppliedState[sourceId] = Object.assign({}, newAppliedState[sourceId], { ledgerRowId: r.id });
          if (setClientCreditLedger) {
            setClientCreditLedger(function(prev) { return (prev || []).concat([mapped]); });
          }
        });
      }
    }

    if (!creditWriteError && ledgerUpdates.length > 0) {
      for (let i = 0; i < ledgerUpdates.length; i++) {
        const upd = ledgerUpdates[i];
        const { error: upErr } = await supabase
          .from('client_credit_ledger')
          .update({ amount: -upd.amount, notes: upd.label || '' })
          .eq('id', upd.id);
        if (upErr) { creditWriteError = upErr.message; break; }
        if (setClientCreditLedger) {
          setClientCreditLedger(function(prev) {
            return (prev || []).map(function(e) {
              return String(e.id) === String(upd.id)
                ? Object.assign({}, e, { amount: -upd.amount, notes: upd.label || '' })
                : e;
            });
          });
        }
      }
    }

    if (!creditWriteError && ledgerDeletes.length > 0) {
      const { error: delErr } = await supabase
        .from('client_credit_ledger')
        .delete()
        .in('id', ledgerDeletes);
      if (delErr) {
        creditWriteError = delErr.message;
      } else if (setClientCreditLedger) {
        setClientCreditLedger(function(prev) {
          return (prev || []).filter(function(e) { return ledgerDeletes.indexOf(e.id) < 0; });
        });
      }
    }

    if (creditWriteError) {
      setBsSummaryError('Summary saved, but credit application failed: ' + creditWriteError);
    } else {
      // Identity-stable (2026-08-16): newAppliedState is a fresh object
      // on EVERY persist — even a no-credit draft autosave ({} → new {}).
      // bsAppliedCredits deps both the autosave and live-preview effects,
      // so swapping in an identical-but-new object re-armed the save
      // every 1.5s and aborted every preview render mid-flight — the
      // infinite loop that kept the July Moskito preview from ever
      // completing. Keep the previous object when nothing changed.
      setBsAppliedCredits(prev => appliedCreditsStateEqual(prev, newAppliedState) ? prev : newAppliedState);
    }

    // Tag the included proposals with this summary's id when sending.
    // Idempotent — only tags ones that are untagged or already point at
    // this same summary. Lets a proposal stop appearing on next month's
    // auto-populated list after this summary has been sent.
    if (status === 'sent' && data) {
      // Retainer draw fires at SEND (option A, 2026-06-30): the period's
      // design work is billed against the retainer when the summary goes
      // out, not when a payment lands. Decoupled from payment recording,
      // which now allocates to the underlying invoices (not the summary),
      // so the old "summary marked paid" trigger no longer applies.
      // Idempotent on (sourceType, sourceId) so re-sends don't double-draw.
      if (projectHasRetainer && saveRetainerEntry && bsSubtotal > 0 && bsSelProj) {
        const alreadyDrawn = retainerLedger.some(function(en) {
          return en.sourceType === 'billing_summary' && en.sourceId === summaryId;
        });
        if (!alreadyDrawn) {
          saveRetainerEntry({
            projectLabel: bsSelProj.label,
            entryDate: new Date().toISOString().slice(0, 10),
            entryType: 'draw',
            amount: -Math.abs(bsSubtotal),
            reference: 'Billing Summary ' + bsMonthName + ' ' + bsYear,
            notes: 'Auto-created when summary sent',
            sourceType: 'billing_summary',
            sourceId: summaryId,
            createdBy: userEmail,
          });
        }
      }

      const tagFailures = [];
      const idsToTag = bsProposalsList
        .map(row => row.proposal.id)
        .filter(id => {
          const p = proposals.find(pr => pr.id === id);
          return p && (!p.billedOnSummaryId || p.billedOnSummaryId === summaryId);
        });
      if (idsToTag.length > 0) {
        // Server-side guard: only tag rows whose billed_on_summary_id is
        // still null OR already points at this same summary. Closes the
        // race where two bookkeepers sending different summaries that
        // each include the same proposal would otherwise both succeed
        // (last-write-wins). The .or() filter below makes the update a
        // no-op for rows already tagged on a different summary.
        const { error: tagErr, data: tagged } = await supabase.from('proposals')
          .update({ billed_on_summary_id: summaryId })
          .in('id', idsToTag)
          .or('billed_on_summary_id.is.null,billed_on_summary_id.eq.' + summaryId)
          .select('id');
        if (tagErr) {
          console.error('Failed to tag proposals on summary send:', tagErr);
          tagFailures.push('proposals');
        } else if (setProposals) {
          const taggedIds = (tagged || []).map(t => t.id);
          setProposals(prev => prev.map(p =>
            taggedIds.includes(p.id) ? Object.assign({}, p, { billedOnSummaryId: summaryId }) : p
          ));
          // Surface if some IDs were claimed by a concurrent send.
          const lost = idsToTag.filter(id => !taggedIds.includes(id));
          if (lost.length > 0) {
            console.warn('Some proposals already tagged by another summary:', lost);
            tagFailures.push('proposals (' + lost.length + ' already tagged elsewhere)');
          }
        }
      }

      // Same idempotent tagging for the invoices on this summary.
      const invoiceIdsToTag = bsInvoicesList
        .map(row => row.invoice.id)
        .filter(id => {
          const inv = invoices.find(x => x.id === id);
          return inv && (!inv.billedOnSummaryId || inv.billedOnSummaryId === summaryId);
        });
      if (invoiceIdsToTag.length > 0) {
        // Server-side guard mirrors the proposals tagging path above.
        const { error: tagErr, data: tagged } = await supabase.from('invoices')
          .update({ billed_on_summary_id: summaryId })
          .in('id', invoiceIdsToTag)
          .or('billed_on_summary_id.is.null,billed_on_summary_id.eq.' + summaryId)
          .select('id');
        if (tagErr) {
          console.error('Failed to tag invoices on summary send:', tagErr);
          tagFailures.push('invoices');
        } else if (setInvoices) {
          const taggedIds = (tagged || []).map(t => t.id);
          setInvoices(prev => prev.map(inv =>
            taggedIds.includes(inv.id) ? Object.assign({}, inv, { billedOnSummaryId: summaryId }) : inv
          ));
          const lost = invoiceIdsToTag.filter(id => !taggedIds.includes(id));
          if (lost.length > 0) {
            console.warn('Some invoices already tagged by another summary:', lost);
            tagFailures.push('invoices (' + lost.length + ' already tagged elsewhere)');
          }
        }
      }

      // Lifecycle cascade — when this summary transitions to 'sent',
      // flip every bundled FIN-type invoice at status='ready' to
      // status='sent' + populate sent_at with the summary's sent
      // timestamp. This is the canonical delivery event for the new
      // model: the FIN's send-to-client moment is the summary's
      // send-to-client moment. Off-cycle FINs (sent directly via the
      // per-FIN Mark Sent button) skip this path; their status was
      // already 'sent' before they ended up tagged on a summary, so
      // the WHERE filter below excludes them.
      //
      // 2026-06-26 (FIN restructure Commit 1): 'draft' dropped from
      // both the in-memory filter and the SQL WHERE clause. The
      // bsInvoicesList filter (above) now excludes 'draft' too, so
      // drafts can't be tagged in invoiceIdsToTag in the first place
      // — the cascade scope here is defense in depth against the
      // invariant. If a draft somehow slips through (e.g., a future
      // bug in the filter), it stays at 'draft' rather than being
      // promoted to 'sent' via the back door.
      //
      // Scoped to status='sent' (not paid; not draft) so saving a
      // summary as draft doesn't accidentally fire the cascade.
      if (status === 'sent' && invoiceIdsToTag.length > 0) {
        const sentAtIso = new Date().toISOString();
        const finIdsToFlip = invoiceIdsToTag.filter(id => {
          const inv = invoices.find(x => x.id === id);
          if (!inv) return false;
          const type = inv.invoiceType || inv.invoice_type || 'final';
          if (type !== 'final') return false;
          return inv.status === 'ready';
        });
        if (finIdsToFlip.length > 0) {
          const { error: flipErr, data: flipped } = await supabase.from('invoices')
            .update({ status: 'sent', sent_at: sentAtIso })
            .in('id', finIdsToFlip)
            .eq('status', 'ready')
            .select('id');
          if (flipErr) {
            console.error('Failed to flip bundled FINs to sent:', flipErr);
            tagFailures.push('FIN status cascade');
          } else if (setInvoices) {
            const flippedIds = (flipped || []).map(t => t.id);
            setInvoices(prev => prev.map(inv =>
              flippedIds.includes(inv.id)
                ? Object.assign({}, inv, { status: 'sent', sentAt: sentAtIso })
                : inv
            ));
          }
        }

        // sent_at backfill (2026-07-01) — any OTHER invoice bundled on
        // this summary that still lacks a sent timestamp gets one now
        // (e.g., a legacy 'sent'-status row imported without sent_at, or
        // a 'partial' row paid before its first summary send). Targeted
        // single-column update — never a full-row save, so it cannot
        // clobber status / billed_on_summary_id the way the removed
        // sendPackage stamp loop did. The .is('sent_at', null) guard
        // keeps re-sends from rewriting history.
        const idsNeedingSentAt = invoiceIdsToTag.filter(id => {
          if (finIdsToFlip.includes(id)) return false;
          const inv = invoices.find(x => x.id === id);
          return inv && !(inv.sentAt || inv.sent_at);
        });
        if (idsNeedingSentAt.length > 0) {
          const { error: stampErr, data: stamped } = await supabase.from('invoices')
            .update({ sent_at: sentAtIso })
            .in('id', idsNeedingSentAt)
            .is('sent_at', null)
            .select('id');
          if (stampErr) {
            console.error('Failed to backfill sent_at on bundled invoices:', stampErr);
          } else if (setInvoices) {
            const stampedIds = (stamped || []).map(t => t.id);
            setInvoices(prev => prev.map(inv =>
              stampedIds.includes(inv.id)
                ? Object.assign({}, inv, { sentAt: sentAtIso })
                : inv
            ));
          }
        }
      }

      // Reimbursable receipts: tag every selected receipt with this
      // summary's id. Activates receipts.billed_on_summary_id (added
      // 2026-05-02, only now written). Once tagged, the receipt no
      // longer appears in future drafts' pickers — the untagged-or-sticky
      // predicate in bsReimbursableReceiptsAll excludes already-tagged
      // receipts unless they were ticked on the current draft.
      const receiptIdsToTag = Array.from(bsReimbursableSelections).filter(id => {
        const r = receipts.find(x => x && x.id === id);
        return r && (!r.billedOnSummaryId || r.billedOnSummaryId === summaryId);
      });
      if (receiptIdsToTag.length > 0) {
        const { error: tagErr, data: tagged } = await supabase.from('receipts')
          .update({ billed_on_summary_id: summaryId })
          .in('id', receiptIdsToTag)
          .or('billed_on_summary_id.is.null,billed_on_summary_id.eq.' + summaryId)
          .select('id');
        if (tagErr) {
          console.error('Failed to tag receipts on summary send:', tagErr);
          tagFailures.push('receipts');
        } else if (setReceipts) {
          const taggedIds = (tagged || []).map(t => t.id);
          setReceipts(prev => prev.map(r =>
            taggedIds.includes(r.id) ? Object.assign({}, r, { billedOnSummaryId: summaryId }) : r
          ));
          const lost = receiptIdsToTag.filter(id => !taggedIds.includes(id));
          if (lost.length > 0) {
            console.warn('Some receipts already tagged by another summary:', lost);
            tagFailures.push('receipts (' + lost.length + ' already tagged elsewhere)');
          }
        }
      }

      // Surface partial-failure to the bookkeeper instead of swallowing.
      // The summary itself is sent; only the bundled-record tagging
      // failed. Re-running Send is safe (the tag updates are idempotent).
      if (tagFailures.length > 0) {
        setBsSummaryError(
          'Summary sent, but tagging failed for: ' + tagFailures.join(', ') +
          '. Re-click Send to retry — these records may reappear on next month\'s auto-list until tagged.'
        );
      }
    }

    return data;
  }

  // Build the PDF generator payload from the current form state.
  // Used by both the explicit Generate-PDF download and the debounced
  // live preview in the right pane.
  //
  // The "Services & Expenses" block (since 2026-05-18) merges design
  // hours with selected reimbursable receipt lines. Receipt descriptions
  // are looked up LIVE from receipts state here — never snapshotted
  // onto the summary row — so editing a receipt's notes propagates to
  // the next render of any summary it's tagged onto. Tests:
  // billingSummaryReceiptLiveRead.test.jsx pins this contract.
  function buildBsPayload() {
    const today = new Date();
    const todayStr = `${['January','February','March','April','May','June','July','August','September','October','November','December'][today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;
    const liveReceiptLines = buildSummaryReceiptLines(
      Array.from(bsReimbursableSelections),
      receipts
    );
    // Expense groups (2026-08-30): fold grouped receipts into named lines
    // for DISPLAY; the raw per-receipt lines still ride the payload as
    // receiptLinesDetail so the frozen manifest keeps full backup detail.
    const displayReceiptLines = applyExpenseGroupsToReceiptLines(
      liveReceiptLines, bsExpensesDisplay.groups
    );
    const servicesAndExpenses = {
      designHours:  parseFloat(bsDesignHours) || 0,
      designAmount: bsDesignAmt,
      receiptLines: displayReceiptLines.map(function(ln) {
        return { description: ln.description, date: ln.date, amount: ln.amount };
      }),
      receiptLinesDetail: liveReceiptLines.map(function(ln) {
        // vendor + cost joined 2026-09-04 so the frozen manifest alone can
        // answer the bookkeeper's "what was in that group" question without
        // re-reading (and re-deriving) the live receipts. Rows frozen before
        // that date carry only receiptId/description/date/amount.
        return { receiptId: ln.receiptId, description: ln.description, date: ln.date,
                 vendor: ln.vendor, cost: ln.cost, amount: ln.amount };
      }),
      amountDue:    Math.round((bsDesignAmt + bsReimbursableTotal) * 100) / 100,
    };
    return {
      date: todayStr,
      billTo: {
        name:    bsClient?.name            || '',
        address: bsClient?.billingAddress  || '',
        city:    bsClient?.billingCity     || '',
        state:   bsClient?.billingState    || '',
        zip:     bsClient?.billingZip      || '',
      },
      forPeriod:         `${bsMonthName} ${bsYear}`,
      // Project identity (2026-07-01, Cimon): the cover must say WHICH
      // project it covers — clients can have several. The renderer puts
      // the display name in the masthead ("HOBACK RESIDENCE #580 · MAY
      // 2026") in place of the generic "Billing Summary" title.
      projectLabel:      (bsSelProj && bsSelProj.label) || '',
      projectDisplay:    (bsSelProj && (bsSelProj.displayName || bsSelProj.label)) || '',
      // Canonical block shape (2026-05-18). The Python renderer reads
      // servicesAndExpenses; older designServices field kept for the
      // brief window where stale callers may still POST.
      servicesAndExpenses: servicesAndExpenses,
      designServices:    { hours: parseFloat(bsDesignHours) || 0, amount: bsDesignAmt },
      additionalServices: bsAdditionalServices.filter(s => s.name && s.amount),
      expenses:           bsExpenses.filter(e => e.description && e.amount),
      // Statement presentation (2026-08-23): when consolidate is on the
      // renderer collapses receipt lines + expense rows into ONE line.
      // Raw lines still ride the payload (frozen-manifest completeness).
      // groups (2026-08-30) are already folded into receiptLines above;
      // carried here so the as-sent archive keeps the setting.
      expensesDisplay:    { consolidate: !!bsExpensesDisplay.consolidate, label: bsExpensesDisplay.label || '', groups: bsExpensesDisplay.groups || [] },
      billingMethod:      projectHasRetainer ? 'retainer' : 'hourly',
      retainerBalance:    bsCurrentRetainer,
      retainerRequest:    projectHasRetainer ? bsRetainerRequest : 0,
      // Per-project retainer threshold (since 2026-05-11). The PDF
      // uses this for the subline wording ("Retainer is requested
      // whenever the current balance falls below $X") and as the
      // fallback target when computing the auto-request amount on
      // legacy callers. Falls back to 60000 if missing.
      retainerThreshold:  bsRetainerThreshold,
      hoursNarratives:    bsHoursNarratives,
      procurement: {
        proposalTotal:    bsProposalsTotal,
        depositRequested: bsProposalsDeposit,
        invoicesDue:      bsProposalsOutstanding,
      },
      proposalsThisMonth: bsProposalsList.map(row => ({
        ref: row.proposal.proposal_ref || '',
        name: row.proposal.proposal_name || '',
        sentDate: row.displayDate,
        totalDeposit: row.totalDeposit,
        totalClient: row.totalClient,
        isOutstanding: row.isOutstanding,
        amountOwed: row.amountOwed,
      })),
      invoicesThisMonth: bsInvoicesList.map(row => ({
        ref:    row.invoice.invoiceRef || row.invoice.invoice_ref || '',
        type:   row.invoice.invoiceType || row.invoice.invoice_type || '',
        name:   invoiceFriendlyName(row.invoice, proposals),
        sentDate: row.displayDate,
        // `amount` is BOTH what the statement prints for the row and the
        // frozen send manifest resolveBillingPackage reconciles against
        // (Rule 10) — one number, one meaning.
        amount: row.amount,
      })),
      // Hours-invoice presence drives the manifest line at the bottom of
      // the cover. True when a finalized hours_invoices row matched the
      // project + period and got auto-attached.
      hasHoursInvoice: !!(bsHoursInvoice && bsHoursInvoice.status === 'Final'),
      // Client credits applied to this summary's procurement section
      // (2026-05-22). Each entry is one row in the PDF's CREDITS group
      // between PROPOSALS and AMOUNT DUE. Application date is "today"
      // (live preview) or the persisted entry_date — per SPEC decision
      // the cover always shows the application date, never the original
      // credit date.
      creditsApplied: Object.keys(bsAppliedCredits).map(function(sourceCreditId) {
        const sel = bsAppliedCredits[sourceCreditId];
        const source = (clientCreditLedger || []).find(function(e) { return e && String(e.id) === String(sourceCreditId); }) || {};
        const todayIso = new Date().toISOString().slice(0, 10);
        return {
          sourceCreditId: sourceCreditId,
          reference:      source.reference || 'Credit',
          appliedDate:    todayIso,
          amount:         parseFloat(sel.amount) || 0,
          // What the row is called on the statement. The renderer also
          // names the section from it when every row agrees.
          description:    String(sel.label || '').trim()
                          || creditPrintLabel(source.entryType),
        };
      }).filter(function(c) { return c.amount > 0; }),
      // Section heading — categorical, from the SOURCE entry types, never
      // the free text a row carries (a heading is a category, not a
      // sentence). See creditsSectionLabel.
      creditsLabel: creditsSectionLabel(
        Object.keys(bsAppliedCredits)
          .filter(function(id) { return (parseFloat(bsAppliedCredits[id].amount) || 0) > 0; })
          .map(function(id) {
            var src = (clientCreditLedger || []).find(function(e) { return e && String(e.id) === String(id); });
            return src && src.entryType;
          })),
      creditsAppliedTotal: bsAppliedCreditsTotal,
      // Procurement amount due gross / net + excess. The Python renderer
      // clamps to $0 and renders the informational note when excess > 0.
      procAmountDueGross:  bsProcAmountDueGross,
      procAmountDueNet:    bsProcAmountDueNet,
      creditsExcess:       bsCreditsExcess,
    };
  }

  async function generateBillingSummary() {
    setBsSummaryGenerating(true);
    setBsSummaryError('');
    try {
      const payload = buildBsPayload();
      const res = await fetch('/api/generate-billing-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'API error');
      const bytes = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
      const blob  = new Blob([bytes], { type: 'application/pdf' });
      const url   = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || 'billing_summary.pdf';
      a.click();
      URL.revokeObjectURL(url);

      // Persist as draft (the bookkeeper marks Sent/Paid separately)
      await persistRecord(bsSavedRecord?.status === 'sent' || bsSavedRecord?.status === 'paid'
        ? bsSavedRecord.status
        : 'draft');
    } catch (e) {
      setBsSummaryError('Could not generate: ' + e.message);
    }
    setBsSummaryGenerating(false);
  }

  async function markAsSent() {
    const result = await persistRecord('sent');
    if (result) setBsLastSavedAt(new Date());
    // Freeze the payload (2026-08-31 — the Hoback July lesson): a summary
    // marked Sent without a document_sends snapshot has NO faithful
    // regeneration path — Billing History falls to buildLegacyCoverPayload,
    // which carries no receipt lines and sweeps in CURRENT invoice
    // activity, printing a statement the client never received. Send
    // Package has frozen its payload since Phase 5A; Mark as Sent now
    // does the same (no PDF bytes here, so storagePath stays '' — regen
    // renders from the frozen payload). Best-effort like sendPackage's:
    // a failure logs but never blocks the status flip.
    try {
      const summaryId = result && result.id;
      if (summaryId && ctx.recordDocumentSend) {
        await ctx.recordDocumentSend({
          documentType: 'billing_summary',
          documentId:   summaryId,
          payload:      buildSummaryPayload(),
          storagePath:  '',
          sentTo:       (bsClient && bsClient.primaryEmail) || (bsClient && bsClient.email) || '',
        });
      }
    } catch (e) {
      console.warn('markAsSent document_sends write failed:', e && e.message);
    }
  }

  // Reopen a SENT summary as a draft (2026-07-20 — Cimon: "is there a
  // way to fix this so that I can redownload an updated package?").
  // The sanctioned revision path for a package the client has NOT paid:
  // flip sent → draft (tagged documents are sticky to their own
  // summary, so everything bundled reappears on the workbench), and
  // delete this send's retainer draw so the NEXT send re-creates it at
  // the corrected amount — the draw's (sourceType, sourceId)
  // idempotency would otherwise skip and leave the first send's stale
  // number (the gap deferred to FOLLOWUPS on 2026-07-20, closed here).
  // The as-sent archive in document_sends is untouched; a re-send adds
  // the next version and Billing History serves the newest.
  // Guards:
  //   - any payment allocated to the summary row OR to any invoice
  //     tagged to it → hard refusal (recorded money must never be
  //     edited underneath);
  //   - another draft already open for this project → refusal (the
  //     one-draft-per-project index would reject the flip mid-air —
  //     the 23505 that derailed the 2026-07-20 recovery).
  async function reopenAsDraft() {
    if (!bsSavedRecord || bsSavedRecord.status !== 'sent' || !bsSelProj) return;
    setBsReopenBusy(true); setBsSummaryError('');
    try {
      const sid = String(bsSavedRecord.id);
      const taggedInvoiceIds = (invoices || [])
        .filter(inv => String(inv.billedOnSummaryId || inv.billed_on_summary_id || '') === sid)
        .map(inv => String(inv.id));
      const paid = (paymentAllocations || []).filter(a => {
        if (!a) return false;
        const dt = a.documentType || a.document_type;
        const did = String(a.documentId != null ? a.documentId : a.document_id);
        if (dt === 'billing_summary' && did === sid) return true;
        if (dt === 'invoice' && taggedInvoiceIds.indexOf(did) >= 0) return true;
        return false;
      });
      if (paid.length > 0) {
        setBsSummaryError('Cannot reopen — ' + paid.length + ' payment allocation'
          + (paid.length === 1 ? '' : 's')
          + ' recorded against this package. Money already recorded must not be edited underneath; bill changes on the next summary or via credits.');
        setBsReopenConfirm(false); setBsReopenBusy(false); return;
      }
      const { data: otherDraft } = await supabase.from('billing_summaries')
        .select('id, year, month')
        .eq('project_label', bsSelProj.label)
        .eq('status', 'draft')
        .maybeSingle();
      if (otherDraft && String(otherDraft.id) !== sid) {
        setBsSummaryError('Cannot reopen — this project already has a draft in progress ('
          + otherDraft.month + '/' + otherDraft.year + '). Send or delete that draft first.');
        setBsReopenConfirm(false); setBsReopenBusy(false); return;
      }
      // Remove this send's retainer draw(s); the re-send re-creates
      // them at the current amount via the existing send-time draw.
      const draws = (retainerLedger || []).filter(en =>
        en && en.sourceType === 'billing_summary' && String(en.sourceId || '') === sid);
      for (const en of draws) {
        if (deleteRetainerEntry) await deleteRetainerEntry(en.id);
      }
      const { error } = await supabase.from('billing_summaries')
        .update({ status: 'draft' })
        .eq('id', bsSavedRecord.id);
      if (error) throw error;
      setBsSavedRecord(Object.assign({}, bsSavedRecord, { status: 'draft' }));
      setBsReopenConfirm(false);
    } catch (e) {
      setBsSummaryError('Reopen failed: ' + (e && e.message ? e.message : String(e)));
    }
    setBsReopenBusy(false);
  }

  // Save the current form state as a draft. The bookkeeper can iterate on
  // the same draft over days/weeks before sending; the partial unique
  // index in Postgres guarantees only one draft per project at a time.
  async function saveDraft() {
    const targetStatus = (bsSavedRecord?.status === 'sent' || bsSavedRecord?.status === 'paid')
      ? bsSavedRecord.status
      : 'draft';
    const result = await persistRecord(targetStatus);
    if (result) setBsLastSavedAt(new Date());
  }

  // Load all drafts (across projects) for the "Continue with Summary" picker.
  async function loadDrafts() {
    const { data, error } = await supabase.from('billing_summaries')
      .select('id, project_label, year, month, updated_at, sent_at')
      .eq('status', 'draft')
      .order('updated_at', { ascending: false })
      .limit(20);
    if (!error && data) setBsDraftsList(data);
  }

  function openDraftPicker() {
    loadDrafts();
    setBsDraftPickerOpen(true);
  }

  function loadDraft(draft) {
    const proj = projects.find(p => p.label === draft.project_label);
    if (proj) {
      setBsProjId(String(proj.id));
      setBsMonth(draft.month);
      setBsYear(draft.year);
      // Fire the hours-invoice lookup explicitly. The control onChange
      // handlers (project/month/year selects) trigger this when the
      // bookkeeper picks values manually, but loading a draft sets state
      // programmatically and bypasses those handlers, leaving
      // bsHoursInvoice null. Without this, sending a freshly-loaded
      // draft would skip bundling the hours invoice into the zip.
      lookupHoursInvoice(String(proj.id), draft.month, draft.year);
    }
    setBsDraftPickerOpen(false);
  }

  async function deleteDraft(draftId) {
    if (!window.confirm('Delete this draft? The form data is gone permanently — receipts and invoices stay available for the next draft.')) return;

    // Release billed_on_summary_id tags BEFORE deleting the row
    // (LIFECYCLES C20; AUDIT_UI_2026-06 S-6; AUDIT_FINANCIAL_2026-06 DR-3).
    // Tags are written at Mark Sent, so a normal draft has none and these
    // are no-ops — but any summary row that ever held tags would otherwise
    // permanently hide its proposals/invoices/receipts from every future
    // draft (the auto-include filters require !billedOnSummaryId, so an
    // orphan tag means "never billed via summary again", silently).
    // Applied-credit rows already self-heal via FK ON DELETE CASCADE
    // (migration 20260522); this is the same cleanup for the three text
    // tag columns, fail-closed: if any untag fails, the row is NOT
    // deleted — a live summary with tags beats a deleted summary with
    // orphans. Sentinel for pre-existing orphans: INTEGRITY_SWEEP §1.6.
    const untagFailures = [];
    for (const table of ['proposals', 'invoices', 'receipts']) {
      const { error: untagErr } = await supabase.from(table)
        .update({ billed_on_summary_id: null })
        .eq('billed_on_summary_id', draftId);
      if (untagErr) untagFailures.push(table + ': ' + untagErr.message);
    }
    if (untagFailures.length > 0) {
      setBsSummaryError('Draft NOT deleted — could not release its attached records (' + untagFailures.join('; ') + '). Try again.');
      return;
    }

    // .select('id') verifies a row actually died (2026-09-04, Cimon: "I
    // try to delete it and it doesn't delete"). An RLS-blocked DELETE
    // returns NO error and ZERO rows — without this check the app
    // reported success while the draft survived, and the conflict
    // banner (which also pauses every save on the form) never lifted.
    const { data: deletedRows, error } = await supabase.from('billing_summaries')
      .delete().eq('id', draftId).select('id');
    if (error) { setBsSummaryError('Could not delete: ' + error.message); return; }
    if (!deletedRows || deletedRows.length === 0) {
      setBsSummaryError('Delete was blocked — the database refused it silently (0 rows deleted). '
        + 'This is almost always row-level security: check that the 20260902 admin-emails '
        + 'migration has been run in the Supabase Dashboard, or delete the row there directly '
        + '(billing_summaries id ' + draftId + ').');
      return;
    }
    // A successful delete lifts the draft-conflict banner immediately —
    // the banner pauses autosave, so leaving it up after the conflict is
    // gone kept the form read-only-in-practice until a period re-pick.
    setBsDraftConflict(prev => (prev && String(prev.id) === String(draftId)) ? null : prev);

    // Mirror the untag in local state so the released records reappear in
    // pickers immediately (without this, a reload is needed to see them).
    const untag = (row) => (row && (row.billedOnSummaryId === draftId || row.billed_on_summary_id === draftId))
      ? Object.assign({}, row, { billedOnSummaryId: null, billed_on_summary_id: null })
      : row;
    if (setProposals) setProposals(prev => (prev || []).map(untag));
    if (setInvoices)  setInvoices(prev => (prev || []).map(untag));
    if (setReceipts)  setReceipts(prev => (prev || []).map(untag));

    // If the deleted draft is the one currently loaded, clear local state too.
    if (bsSavedRecord?.id === draftId) setBsSavedRecord(null);
    loadDrafts();
  }

  // F3: synthesize per-unit pricing for assemblies so the proposal PDF
  // renders the correct line totals. Mirrors resolveItemsForPdf in
  // ProposalsTab2 -- inlined here to keep the change localized.
  function bsResolveItemsForPdf(ids) {
    return allItems
      .filter(it => ids.indexOf(it.id) >= 0)
      .map(it => {
        const comps = allItems.filter(c => c.parentId === it.id);
        if (comps.length === 0) return it;
        const qty = parseQty(it.qty) || 1;
        const netTotal = assemblyNet(it, allItems);
        const clientTotal = assemblyClient(it, allItems);
        const netPerUnit = netTotal / qty;
        const synthMarkup = netPerUnit > 0 ? ((clientTotal / netTotal) - 1) * 100 : 30;
        return Object.assign({}, it, {
          netPrice: Math.round(netPerUnit * 100) / 100,
          markupPct: Math.round(synthMarkup * 100) / 100,
        });
      });
  }

  // Mirrors ProposalsTab2.buildProposalPayload: resolve component state
  // (items / client / project / allItems) and delegate to the canonical
  // pure builder. Both /api/generate-proposal POSTs in this file MUST
  // route through here — never assemble the body inline. The Python
  // generator validates the new payload shape and rejects v1-shaped
  // bodies (productTotal=0, depositTotal=0) with a 500 error.
  function buildBsProposalPayload(p) {
    const pItems = bsResolveItemsForPdf(p.items || []);
    const projObj = projects.find(pr => pr.label === p.project) || { label: p.project };
    const client = clients.find(c => c.id === p.clientId)
      || (projObj.clientId ? clients.find(c => c.id === projObj.clientId) : null)
      || {};
    return buildProposalPayloadPure({
      proposal: p,
      items:    pItems,
      client:   client,
      project:  projObj,
      allItems: allItems,
    });
  }

  async function previewBundledProposal(p) {
    const token = ++bsBundledFetchToken.current;
    setBsPreviewSel({ type: 'proposal', id: p.id });
    setBsBundledPdfLoading(true);
    setBsBundledPdfError('');
    setBsBundledPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      const res = await fetch('/api/generate-proposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBsProposalPayload(p)),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'PDF failed');
      }
      const blob = await res.blob();
      if (token !== bsBundledFetchToken.current) {
        // A newer bundled fetch superseded this one before it finished.
        return;
      }
      const url = URL.createObjectURL(blob);
      setBsBundledPdfUrl(url);
    } catch (e) {
      if (token === bsBundledFetchToken.current) setBsBundledPdfError(e.message || 'Preview failed');
    }
    if (token === bsBundledFetchToken.current) setBsBundledPdfLoading(false);
  }

  function backToCover() {
    bsBundledFetchToken.current += 1; // invalidate any in-flight bundled fetch
    setBsPreviewSel({ type: 'cover' });
    setBsBundledPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setBsBundledPdfError('');
    setBsBundledPdfLoading(false);
  }

  // Preview an invoice (deposit / final / reimbursable) in the right pane.
  // Mirrors the InvoicesTab fetch logic but writes into the workbench's
  // bundled-preview state instead of an embedded modal.
  async function previewBundledInvoice(inv) {
    const token = ++bsBundledFetchToken.current;
    setBsPreviewSel({ type: 'invoice', id: inv.id });
    setBsBundledPdfLoading(true);
    setBsBundledPdfError('');
    setBsBundledPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      const snapshot = inv.lineItems || inv.line_items || [];
      if (!snapshot || snapshot.length === 0) throw new Error('No line items snapshot on this invoice.');
      const invoiceType = inv.invoiceType || inv.invoice_type || 'deposit';
      let endpoint, payload, parseAsBlob = true;

      if (invoiceType === 'reimbursable') {
        const client = clients.find(c => c.id === (inv.clientId || inv.client_id));
        const projLabel = inv.project || inv.project_label || '';
        const projectRec = projects.find(pr => pr.label === projLabel);
        const projectDisplay = (projectRec && projectRec.displayName) || projLabel;
        const createdAtStr = String(inv.createdAt || inv.created_at || new Date().toISOString());
        const d = new Date(createdAtStr);
        const period = isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const invoiceDate = isNaN(d.getTime())
          ? createdAtStr.slice(0, 10)
          : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        endpoint = '/api/generate-reimbursable-invoice';
        payload = {
          invoiceRef: inv.invoiceRef || inv.invoice_ref || '',
          invoiceDate, projectDisplay,
          billTo: {
            name:    inv.clientName || inv.client_name || (client ? client.name : ''),
            address: client ? (client.billingAddress || '') : '',
            city:    client ? (client.billingCity    || '') : '',
            state:   client ? (client.billingState   || '') : '',
            zip:     client ? (client.billingZip     || '') : '',
          },
          period, lineItems: snapshot,
          subtotal: parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0,
          notes: inv.notes || '', markupPct: 15,
        };
        parseAsBlob = false;
      } else {
        endpoint = '/api/generate-deposit-invoice';
        const productTotal = parseFloat(inv.productTotal) || 0;
        const subtotal     = parseFloat(inv.subtotal)     || 0;
        const feesTotal    = parseFloat(inv.feesTotal)    || Math.round((subtotal - productTotal) * 100) / 100;
        // Per-fee breakdown columns added 2026-05-12. Persisted rows after
        // that date carry the breakout; legacy rows have all zeros and
        // fall back to a single 'Fees & Sales Tax' custom line.
        const feeFreight      = parseFloat(inv.feeFreightTot)      || 0;
        const feeCrating      = parseFloat(inv.feeCratingTot)      || 0;
        const feeInstallation = parseFloat(inv.feeInstallationTot) || 0;
        const feeTariff       = parseFloat(inv.feeTariffTot)       || 0;
        const feeSalesTax     = parseFloat(inv.feeSalesTaxTot)     || 0;
        const feeCustomArr    = Array.isArray(inv.feeCustom) ? inv.feeCustom : [];
        const hasPerFeeBreakdown = feeFreight + feeCrating + feeInstallation + feeTariff + feeSalesTax > 0
          || feeCustomArr.length > 0;
        payload = {
          invoiceRef:  inv.invoiceRef || inv.invoice_ref || '',
          invoiceType,
          projectLabel:   inv.project || inv.project_label || '',
          projectDisplay: inv.project || inv.project_label || '',
          billToName: inv.clientName || inv.client_name || '',
          billToAddr: inv.billToAddr || '',
          siteAddr:   inv.siteAddr || '',
          lineItems: snapshot,
          productTotal,
          feeFreightTot:      feeFreight,
          feeCratingTot:      feeCrating,
          feeInstallationTot: feeInstallation,
          feeTariffTot:       feeTariff,
          feeSalesTaxTot:     feeSalesTax,
          feeCustom: hasPerFeeBreakdown
            ? feeCustomArr
            : (feesTotal > 0 ? [{ label: 'Fees & Sales Tax', amount: feesTotal }] : []),
          feesLumped: !hasPerFeeBreakdown && feesTotal > 0,
          // Card fee — one source, invoiceCcFeeTot in utils.js.
          feeCcProcessingTot: invoiceCcFeeTot(inv, {
            subtotal: subtotal, productTotal: productTotal, feesTotal: feesTotal,
          }),
          subtotal,
          depositTotal: parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0,
          balanceRemaining: 0,
          notes: inv.notes || '',
          date: (inv.createdAt || inv.created_at || new Date().toISOString()).slice(0, 10),
        };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'PDF failed');
      }
      let blob;
      if (parseAsBlob) {
        blob = await res.blob();
      } else {
        const result = await res.json();
        const bytes = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
        blob = new Blob([bytes], { type: 'application/pdf' });
      }
      if (token !== bsBundledFetchToken.current) {
        return; // a newer click already replaced the selection
      }
      setBsBundledPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      if (token === bsBundledFetchToken.current) setBsBundledPdfError(e.message || 'Preview failed');
    }
    if (token === bsBundledFetchToken.current) setBsBundledPdfLoading(false);
  }

  // ── SEND PACKAGE ─────────────────────────────────────────────────────────
  // One click to: (a) mark this Billing Summary Sent, (b) cascade Sent date
  // to every dependent doc for the period, (c) bundle every PDF into a ZIP
  // and download. Pending = unpaid invoices for this project; reimbursable
  // invoices filtered to the period; deposit/final invoices included
  // regardless of when generated since the client still owes them.
  const [bsPackageGenerating, setBsPackageGenerating] = useState(false);

  async function loadJSZip() {
    if (window.JSZip) return window.JSZip;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = () => resolve(window.JSZip);
      script.onerror = () => reject(new Error('Could not load JSZip'));
      document.head.appendChild(script);
    });
  }

  function safeFilename(s) {
    return String(s || '').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  }

  async function fetchPdfBytes(endpoint, payload, parseMode) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `${endpoint} failed`);
    }
    if (parseMode === 'json') {
      const data = await res.json();
      return Uint8Array.from(atob(data.pdf), c => c.charCodeAt(0));
    }
    const blob = await res.blob();
    return new Uint8Array(await blob.arrayBuffer());
  }

  function buildSummaryPayload() {
    const today = new Date();
    const todayStr = `${['January','February','March','April','May','June','July','August','September','October','November','December'][today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;
    const liveReceiptLines = buildSummaryReceiptLines(
      Array.from(bsReimbursableSelections),
      receipts
    );
    return {
      date: todayStr,
      billTo: {
        name:    bsClient?.name            || '',
        address: bsClient?.billingAddress  || '',
        city:    bsClient?.billingCity     || '',
        state:   bsClient?.billingState    || '',
        zip:     bsClient?.billingZip      || '',
      },
      forPeriod:         `${bsMonthName} ${bsYear}`,
      // Project identity (2026-07-01, Cimon): the cover must say WHICH
      // project it covers — clients can have several. The renderer puts
      // the display name in the masthead ("HOBACK RESIDENCE #580 · MAY
      // 2026") in place of the generic "Billing Summary" title.
      projectLabel:      (bsSelProj && bsSelProj.label) || '',
      projectDisplay:    (bsSelProj && (bsSelProj.displayName || bsSelProj.label)) || '',
      servicesAndExpenses: {
        designHours:  parseFloat(bsDesignHours) || 0,
        designAmount: bsDesignAmt,
        // Expense groups folded for display; raw detail rides alongside —
        // parity with buildBsPayload (2026-08-30).
        receiptLines: applyExpenseGroupsToReceiptLines(liveReceiptLines, bsExpensesDisplay.groups).map(function(ln) {
          return { description: ln.description, date: ln.date, amount: ln.amount };
        }),
        receiptLinesDetail: liveReceiptLines.map(function(ln) {
          // Parity with buildBsPayload above — vendor + cost joined
          // 2026-09-04. Both builders must emit the same manifest shape or
          // the live preview and the frozen as-sent record disagree.
          return { receiptId: ln.receiptId, description: ln.description, date: ln.date,
                   vendor: ln.vendor, cost: ln.cost, amount: ln.amount };
        }),
        amountDue:    Math.round((bsDesignAmt + bsReimbursableTotal) * 100) / 100,
      },
      designServices:    { hours: parseFloat(bsDesignHours) || 0, amount: bsDesignAmt },
      additionalServices: bsAdditionalServices.filter(s => s.name && s.amount),
      expenses:           bsExpenses.filter(e => e.description && e.amount),
      // Parity with buildBsPayload (2026-08-23) — the send freezes this
      // payload, so the as-sent archive keeps the consolidation setting.
      expensesDisplay:    { consolidate: !!bsExpensesDisplay.consolidate, label: bsExpensesDisplay.label || '', groups: bsExpensesDisplay.groups || [] },
      billingMethod:      projectHasRetainer ? 'retainer' : 'hourly',
      retainerBalance:    bsCurrentRetainer,
      // Parity with buildBsPayload (2026-08-05 — the filed
      // buildSummaryPayload retainerRequest gap): without this the
      // package/ZIP cover ignored the workbench's request override and
      // re-derived the $60K ask from the threshold.
      retainerRequest:    projectHasRetainer ? bsRetainerRequest : 0,
      retainerThreshold:  bsRetainerThreshold,
      hoursNarratives:    bsHoursNarratives,
      procurement: {
        proposalTotal:    bsProposalsTotal,
        depositRequested: bsProposalsDeposit,
        invoicesDue:      bsProposalsOutstanding,
      },
      proposalsThisMonth: bsProposalsList.map(row => ({
        ref: row.proposal.proposal_ref || '',
        name: row.proposal.proposal_name || '',
        sentDate: row.displayDate,
        totalDeposit: row.totalDeposit,
        totalClient: row.totalClient,
        isOutstanding: row.isOutstanding,
        amountOwed: row.amountOwed,
      })),
      invoicesThisMonth: bsInvoicesList.map(row => ({
        ref:    row.invoice.invoiceRef || row.invoice.invoice_ref || '',
        type:   row.invoice.invoiceType || row.invoice.invoice_type || '',
        name:   invoiceFriendlyName(row.invoice, proposals),
        sentDate: row.displayDate,
        // `amount` is BOTH what the statement prints for the row and the
        // frozen send manifest resolveBillingPackage reconciles against
        // (Rule 10) — one number, one meaning.
        amount: row.amount,
      })),
      hasHoursInvoice: !!(bsHoursInvoice && bsHoursInvoice.status === 'Final'),
      // Client credits applied to this summary's procurement section
      // (parity with buildBsPayload's credit fields — added 2026-05-27
      // to fix the Send Package PDF dropping credits). The Python
      // renderer reads these to render the CREDITS sub-section between
      // PROPOSALS/INVOICES and AMOUNT DUE, and to subtract from the
      // procurement AMOUNT DUE. Without them, the sent package's cover
      // shows the gross procurement total — under-applies credits the
      // bookkeeper already linked to this summary.
      creditsApplied: Object.keys(bsAppliedCredits).map(function(sourceCreditId) {
        const sel = bsAppliedCredits[sourceCreditId];
        const source = (clientCreditLedger || []).find(function(e) { return e && String(e.id) === String(sourceCreditId); }) || {};
        const todayIso = new Date().toISOString().slice(0, 10);
        return {
          sourceCreditId: sourceCreditId,
          reference:      source.reference || 'Credit',
          appliedDate:    todayIso,
          amount:         parseFloat(sel.amount) || 0,
          // What the row is called on the statement. The renderer also
          // names the section from it when every row agrees.
          description:    String(sel.label || '').trim()
                          || creditPrintLabel(source.entryType),
        };
      }).filter(function(c) { return c.amount > 0; }),
      // Section heading — categorical, from the SOURCE entry types, never
      // the free text a row carries (a heading is a category, not a
      // sentence). See creditsSectionLabel.
      creditsLabel: creditsSectionLabel(
        Object.keys(bsAppliedCredits)
          .filter(function(id) { return (parseFloat(bsAppliedCredits[id].amount) || 0) > 0; })
          .map(function(id) {
            var src = (clientCreditLedger || []).find(function(e) { return e && String(e.id) === String(id); });
            return src && src.entryType;
          })),
      creditsAppliedTotal: bsAppliedCreditsTotal,
      procAmountDueGross:  bsProcAmountDueGross,
      procAmountDueNet:    bsProcAmountDueNet,
      creditsExcess:       bsCreditsExcess,
    };
  }

  // opts.skipDraftGate — set ONLY by the draft-FIN warning modal's "Send
  // without them" action. Since 2026-07-27 the toolbar routes through the
  // bsSendConfirm modal (official sends require confirmation), which
  // invokes sendPackage with no args — the skip stays an explicit
  // property check, never a truthy-first-arg check.
  async function sendPackage(opts) {
    if (!bsSelProj) return;
    const skipDraftGate = !!(opts && opts.skipDraftGate === true);
    // Preview mode (2026-07-20 — Cimon: "you cannot download the whole
    // package without kicking the send button"). previewOnly builds the
    // IDENTICAL zip from current data but records NOTHING: no
    // persistRecord('sent'), no tagging/cascade/retainer draw (all live
    // inside the sent persist), no document_sends archives, no
    // hours-invoice sent_at stamp. The zip filename carries a _preview
    // suffix so the reviewed file can't be mistaken for the delivered
    // one. Sending remains the ONLY commitment moment. The draft-FIN
    // gate is also skipped in preview — its confirm path continues into
    // a real send, which a preview must never trigger.
    const previewOnly = !!(opts && opts.previewOnly === true);
    // Draft-FIN visibility gate (2026-07-01). Under the manual Mark Ready
    // lifecycle a draft FIN is deliberately NOT on the cover — but before
    // this gate existed, sendPackage silently bundled draft FIN PDFs into
    // the zip AND flipped them to 'sent' without ever listing them on the
    // cover or tagging billed_on_summary_id (the May 2026 Hoback incident:
    // 9 FINs delivered as attachments the cover never mentioned). Drafts
    // are now excluded from the package entirely (the zip bundles exactly
    // the cover's invoice list — see pendingProcurement below); this
    // modal makes the exclusion VISIBLE so a forgotten Mark Ready is a
    // conscious choice, never a silent one. DEP drafts are exempt: DEPs
    // are internal-only records (the proposal is the client-facing
    // deposit vehicle), so a draft DEP is normal state, not a miss.
    if (!skipDraftGate && !previewOnly) {
      const summaryIdForGate = bsSavedRecord?.id || null;
      const draftFins = (ctx.invoices || []).filter(inv =>
        (inv.invoiceType || inv.invoice_type) === 'final'
        && String(inv.status || '').toLowerCase() === 'draft'
        && (inv.project || inv.project_label) === bsSelProj.label
        && !bsInvoicesExcluded[inv.id]
        && (!inv.billedOnSummaryId || inv.billedOnSummaryId === summaryIdForGate));
      if (draftFins.length > 0) {
        setBsDraftFinWarn(draftFins);
        return;
      }
    }
    setBsPackageGenerating(true);
    setBsSummaryError('');
    try {
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      const periodTag = `${bsYear}-${String(bsMonth).padStart(2, '0')}`;
      const projTag = safeFilename(bsSelProj.label);

      // 1. Generate + add the Billing Summary cover. Capture the bytes
      //    AND the payload — Phase 5A wires both into a document_sends
      //    row after persistRecord('sent') succeeds (step 5 below). The
      //    storage upload and the document_sends INSERT are deferred to
      //    after step 5 because both depend on bsSavedRecord.id, which
      //    persistRecord('sent') guarantees is populated.
      const summaryPayload = buildSummaryPayload();
      let coverBytes = null;
      try {
        coverBytes = await fetchPdfBytes('/api/generate-billing-summary', summaryPayload, 'json');
        zip.file(`00_${periodTag}_${projTag}_billing_summary.pdf`, coverBytes);
      } catch (e) { console.warn('summary PDF failed:', e); }

      // 2. Designer Hours invoice (if finalized hours invoice exists for period)
      if (bsHoursInvoice && bsHoursInvoice.status === 'Final') {
        try {
          // STORED-FIRST (2026-09-04). Save to Records uploads the exact
          // PDF to invoice-pdfs/{invoice_number}.pdf — the package bundles
          // THAT file, so what the client receives is byte-identical to
          // what was finalized on Designer Hours. The previous inline
          // payload rebuilt from bsHoursNarratives: always phase-grouped
          // (never the person format), stale when narratives were loaded
          // before a re-finalize, and headerless — the 2026-09-04 Hoback
          // package carried $16,315.05 against the saved $16,451.45.
          let hoursBytes = null;
          try {
            const { data: storedBlob, error: dlErr } = await supabase.storage
              .from('invoice-pdfs')
              .download(bsHoursInvoice.invoice_number + '.pdf');
            if (!dlErr && storedBlob) hoursBytes = new Uint8Array(await storedBlob.arrayBuffer());
          } catch (e) { /* fall through to regen */ }
          if (!hoursBytes) {
            // Regen fallback — the canonical row-based builder, person-
            // aware via the client's hoursGrouping (never an inline payload).
            const hoursPayload = buildHoursInvoiceRegenPayload(bsHoursInvoice, {
              name:    bsClient?.name            || '',
              address: bsClient?.billingAddress  || '',
              city:    bsClient?.billingCity     || '',
              state:   bsClient?.billingState    || '',
              zip:     bsClient?.billingZip      || '',
            }, { client: bsClient, project: bsSelProj });
            hoursBytes = await fetchPdfBytes('/api/generate-hours-invoice', hoursPayload, 'json');
          }
          zip.file(`01_${periodTag}_${projTag}_design_hours.pdf`, hoursBytes);
        } catch (e) { console.warn('hours PDF failed:', e); }
      }

      // 3. Reimbursable receipts are now picked directly into the cover's
      // Services & Expenses block — no separate reimbursable invoice
      // PDFs to bundle. The deprecated BillToClientModal flow (2026-05-18
      // removal) used to emit one /api/generate-reimbursable-invoice PDF
      // per RE invoice here. Historical RE invoices still render in the
      // InvoicesTab detail modal (view-only); they just don't auto-bundle
      // into a fresh summary's send package anymore.

      // 3.5. Proposals on this summary -- bundle each proposal's PDF so the
      // client receives the underlying agreement alongside the cover doc.
      // Each bsProposalsList row carries the full proposal record at
      // row.proposal -- the previous version looked up `row.id` which
      // doesn't exist, silently skipping every proposal.
      let propIdx = 0;
      for (const row of bsProposalsList) {
        const p = row.proposal;
        if (!p) continue;
        try {
          const propPayload = buildBsProposalPayload(p);
          const bytes = await fetchPdfBytes('/api/generate-proposal', propPayload, 'blob');
          zip.file(`${String(20 + propIdx).padStart(2, '0')}_proposal_${safeFilename(p.proposal_ref || p.id || 'p')}.pdf`, bytes);
          propIdx += 1;
          // Phase 5A.3 (2026-07-02) — archive the EXACT proposal PDF the
          // client receives in this package: upload to the proposals
          // bucket + freeze the payload in document_sends. Best-effort:
          // failure logs and never blocks the send.
          try {
            if (!previewOnly && ctx.recordDocumentSend) {
              const pSentAt = new Date();
              const pPath = proposalStoragePath(p.id, pSentAt);
              const pBlob = bytes instanceof Blob ? bytes : new Blob([bytes], { type: 'application/pdf' });
              const pUp = await supabase.storage
                .from('proposals')
                .upload(pPath.replace(/^proposals\//, ''), pBlob, {
                  contentType: 'application/pdf', upsert: false,
                });
              if (pUp.error) console.warn('proposal PDF archive upload failed:', pUp.error.message);
              await ctx.recordDocumentSend({
                documentType: 'proposal',
                documentId:   p.id,
                payload:      propPayload,
                storagePath:  pUp.error ? '' : pPath,
                sentTo:       (bsClient && (bsClient.primaryEmail || bsClient.email)) || '',
              });
            }
          } catch (archErr) {
            console.warn('proposal document_sends write failed:', archErr && archErr.message);
          }
        } catch (e) { console.warn('proposal PDF failed:', e); }
      }

      // 4. Deposit/final invoice PDFs — EXACTLY the invoices listed on the
      // cover (2026-07-01). The zip set derives from bsInvoicesList, the
      // same memo the cover renders from, so the two can never diverge
      // again. Pre-fix this was an independent filter (any deposit/final
      // not paid/void) that also admitted draft + cancelled invoices —
      // draft FINs shipped as attachments the cover never listed (May
      // 2026 Hoback incident). bsInvoicesList already handles: status
      // (draft/paid/void/cancelled out), manual exclusions, other-summary
      // tags, and retainer-reimbursable suppression. The type filter here
      // just keeps non-procurement rows (reimbursables) out of the
      // deposit-invoice PDF renderer, matching prior behavior.
      const pendingProcurement = bsInvoicesList
        .map(row => row.invoice)
        .filter(inv => inv.invoiceType === 'deposit' || inv.invoiceType === 'final');
      let procIdx = 0;
      for (const inv of pendingProcurement) {
        try {
          const snapshot = inv.lineItems || inv.line_items || [];
          if (snapshot.length === 0) continue;  // nothing to render
          const productTotal = parseFloat(inv.productTotal) || 0;
          const subtotal     = parseFloat(inv.subtotal)     || 0;
          const feesTotal    = parseFloat(inv.feesTotal)    || Math.round((subtotal - productTotal) * 100) / 100;
          // Per-fee breakdown (added 2026-05-12). Persisted rows carry the
          // breakout; legacy rows have all zeros and fall back to the
          // single 'Fees & Sales Tax' line below.
          const pkgFeeFreight      = parseFloat(inv.feeFreightTot)      || 0;
          const pkgFeeCrating      = parseFloat(inv.feeCratingTot)      || 0;
          const pkgFeeInstallation = parseFloat(inv.feeInstallationTot) || 0;
          const pkgFeeTariff       = parseFloat(inv.feeTariffTot)       || 0;
          const pkgFeeSalesTax     = parseFloat(inv.feeSalesTaxTot)     || 0;
          const pkgFeeCustom       = Array.isArray(inv.feeCustom) ? inv.feeCustom : [];
          const pkgHasBreakdown    = pkgFeeFreight + pkgFeeCrating + pkgFeeInstallation + pkgFeeTariff + pkgFeeSalesTax > 0
            || pkgFeeCustom.length > 0;
          const procPayload = {
            invoiceRef:      inv.invoiceRef || inv.invoice_ref || '',
            invoiceType:     inv.invoiceType,
            projectLabel:    inv.project || inv.project_label || '',
            projectDisplay:  bsSelProj.displayName || bsSelProj.label,
            billToName:      inv.clientName || inv.client_name || (bsClient?.name || ''),
            billToAddr:      inv.billToAddr || '',
            siteAddr:        inv.siteAddr || '',
            lineItems:       snapshot,
            productTotal:    productTotal,
            feeFreightTot:      pkgFeeFreight,
            feeCratingTot:      pkgFeeCrating,
            feeInstallationTot: pkgFeeInstallation,
            feeTariffTot:       pkgFeeTariff,
            feeSalesTaxTot:     pkgFeeSalesTax,
            feeCustom: pkgHasBreakdown
              ? pkgFeeCustom
              : (feesTotal > 0 ? [{ label: 'Fees & Sales Tax', amount: feesTotal }] : []),
            feesLumped: !pkgHasBreakdown && feesTotal > 0,
            // Card fee — one source, invoiceCcFeeTot in utils.js.
            feeCcProcessingTot: invoiceCcFeeTot(inv, {
              subtotal: subtotal, productTotal: productTotal, feesTotal: feesTotal,
            }),
            subtotal:        subtotal,
            depositTotal:    parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0,
            balanceRemaining: 0,
            notes:           inv.notes || '',
            date:            (inv.createdAt || inv.created_at || new Date().toISOString()).slice(0, 10),
          };
          const bytes = await fetchPdfBytes('/api/generate-deposit-invoice', procPayload, 'blob');
          zip.file(`${String(10 + procIdx).padStart(2, '0')}_${inv.invoiceType}_${safeFilename(inv.invoiceRef || inv.invoice_ref || 'inv')}.pdf`, bytes);
          procIdx += 1;
        } catch (e) { console.warn('procurement PDF failed:', e); }
      }

      // 5. Persist Billing Summary as Sent (skipped entirely in preview —
      // the sent persist carries the tagging, ready→sent cascade, and
      // retainer draw, none of which a preview may fire).
      if (!previewOnly) await persistRecord('sent');

      // 5a. Phase 5A — write document_sends row freezing the payload +
      //     uploading the cover PDF to the billing-summaries bucket.
      //     The bookkeeper-side cover regeneration (BillingHistorySection)
      //     reads the frozen payload back; the storage path supplies the
      //     audit-grade as-sent PDF. Re-sends append a new row; "most
      //     recent" wins for cover-regen, every row appears in Send
      //     History. Best-effort: failure here logs but does not abort
      //     the rest of the send (zip still downloads, dependents still
      //     get marked sent), so a transient storage/RLS hiccup can't
      //     block the client delivery the bookkeeper is mid-flight on.
      try {
        const summaryId = bsSavedRecord && bsSavedRecord.id;
        if (!previewOnly && summaryId && coverBytes && ctx.recordDocumentSend) {
          const sentAt = new Date();
          const storagePath = billingSummaryStoragePath(summaryId, sentAt);
          const blob = new Blob([coverBytes], { type: 'application/pdf' });
          const up = await supabase.storage
            .from('billing-summaries')
            .upload(storagePath.replace(/^billing-summaries\//, ''), blob, {
              contentType: 'application/pdf', upsert: false,
            });
          if (up.error) {
            console.warn('billing-summary PDF upload failed:', up.error.message);
          }
          await ctx.recordDocumentSend({
            documentType: 'billing_summary',
            documentId:   summaryId,
            payload:      summaryPayload,
            storagePath:  up.error ? '' : storagePath,
            sentTo:       (bsClient && bsClient.primaryEmail) || (bsClient && bsClient.email) || '',
          });
        }
      } catch (sendErr) {
        console.warn('document_sends write failed:', sendErr && sendErr.message);
      }

      // 6. Mark dependents Sent
      const sentISO = new Date().toISOString();

      // Hours invoice
      if (!previewOnly && bsHoursInvoice && bsHoursInvoice.id && !bsHoursInvoice.sent_at) {
        await supabase.from('hours_invoices').update({ sent_at: sentISO }).eq('id', bsHoursInvoice.id);
      }
      // (Reimbursable invoices used to be marked sent here. The 2026-05-18
      //  reimbursable-invoice deprecation removed the surrounding
      //  reimbThisMonth declaration but left this forEach orphaned —
      //  throwing ReferenceError on every send-package call. Receipts
      //  now flow through the cover's Services & Expenses block and the
      //  receipt picker's billed_on_summary_id tagging in persistRecord;
      //  there is no longer any reimbursable invoice to stamp sent here.)
      //
      // (A second stamp loop lived here until 2026-07-01: it walked
      //  pendingProcurement calling saveInvoice with a full stale row —
      //  silently flipping draft FINs to 'sent' (the May 2026 Hoback
      //  incident) and, because it ran AFTER persistRecord('sent') with
      //  objects captured BEFORE it, clobbering the just-written
      //  billed_on_summary_id / status back to their pre-send values.
      //  Removed: persistRecord's send block is now the ONLY place
      //  bundled invoices get status/sent_at writes — the ready→sent
      //  cascade plus a targeted sent_at backfill, both against fresh
      //  DB state, never a full-row save.)

      // 7. Download the ZIP
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${periodTag}_${projTag}_package${previewOnly ? '_preview' : ''}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setBsSummaryError('Package generation failed: ' + (e.message || 'unknown'));
    }
    setBsPackageGenerating(false);
  }
  // ── ALLOCATION OPERATIONS ──────────────────────────────────────────────
  // Persist the lifecycle status on the billing_summaries row from the
  // relational allocation set. 'paid' when sum-of-allocations meets the asked
  // amount, otherwise walks back to 'sent' (or 'draft' if it was draft).
  async function syncSummaryStatus(allocs) {
    if (!bsSavedRecord) return null;
    const amount = parseFloat(bsSubtotal) || 0;
    const nextStatus = derivePaidStatus(
      allocs, 'billing_summary', bsSavedRecord.id, amount, bsSavedRecord.status);
    if (nextStatus === bsSavedRecord.status) return bsSavedRecord;
    const { data, error } = await supabase.from('billing_summaries')
      .update({ status: nextStatus }).eq('id', bsSavedRecord.id).select().single();
    if (error) { setBsSummaryError(error.message); return bsSavedRecord; }
    if (data) { setBsSavedRecord(data); }
    return data || bsSavedRecord;
  }

  // PaymentsPanel onRecordPayment(payment, allocations) bridge. After
  // persisting via context, sync billing_summaries.status. Side effect: when
  // status flips to 'paid' for the first time and the project is on retainer,
  // auto-create the retainer draw (idempotent against sourceType/sourceId).
  async function onPanelRecordPayment(payment, allocations) {
    if (!recordClientPayment || !bsSavedRecord) return;
    const res = await recordClientPayment(payment, allocations);
    if (!res || !res.ok) return;
    const nextAllocs = paymentAllocations.concat(res.allocations || []);
    await syncSummaryStatus(nextAllocs);
    // Retainer draw no longer fires on payment — it's created when the
    // summary is SENT (option A, 2026-06-30). See persistRecord's
    // `status === 'sent'` block.
  }

  // PaymentsPanel onRemoveAllocation(allocationId) bridge. After removal,
  // the summary may drop below fully-paid → walk status back. If the status
  // did drop and we had auto-created a retainer draw for this summary, walk
  // that draw back too.
  async function onPanelRemoveAllocation(allocationId) {
    if (!removePaymentAllocation || !bsSavedRecord) return;
    await removePaymentAllocation(allocationId);
    const nextAllocs = paymentAllocations.filter(a => a.id !== allocationId);
    await syncSummaryStatus(nextAllocs);
    // Retainer draw reversal no longer fires on payment removal — the draw
    // is tied to SEND, not payment (option A). Un-sending / deleting a sent
    // summary should reverse the draw; that's a known follow-up (rare in
    // practice — sent summaries are not normally walked back).
  }

  // ── RENDER ──
  // Two-pane workbench: composition form on the left, PDF preview on the
  // right. Phase F2 will wire the right pane to a debounced live cover
  // regeneration; F3 makes bundled items clickable to swap which PDF is
  // shown. Sent-package history moved to its own sub-tab.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 620px) 1fr', gap: 20, padding: '20px 18px', maxWidth: 1400, alignItems: 'start' }}>

      {/* LEFT PANE — composition form */}
      <div>

      {/* Off-cycle escape hatch (2026-07-04) — a proposals-only package
          sent outside the monthly cycle. Its own modal + flow; the
          monthly workbench below is untouched. Same role gate as Mark
          Sent (owner/admin). */}
      {(isOwner || hasAdminAccess(userEmail, firmSettings)) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <button onClick={() => setOffCycleOpen(true)}
            style={{
              padding: '5px 12px', border: '0.5px solid #D8D7D2', borderRadius: 4,
              background: '#FFFFFF', color: '#111111', fontSize: 11,
              fontFamily: 'inherit', cursor: 'pointer',
            }}>
            Send off-cycle package →
          </button>
        </div>
      )}
      {offCycleOpen && (
        <OffCyclePackageModal onClose={() => setOffCycleOpen(false)} />
      )}

      {/* Project + Period */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
        <div>
          <label style={lbl}>PROJECT</label>
          <select value={bsProjId}
            onChange={e => { setBsProjId(e.target.value); if (e.target.value) lookupHoursInvoice(e.target.value, bsMonth, bsYear); }}
            style={{ ...inp, width: '100%' }}>
            <option value="">— Select project —</option>
            {activeProjects(projects, (projects.find(pr => String(pr.id) === String(bsProjId)) || {}).label).map(proj => (
              <option key={proj.id} value={proj.id}>
                {proj.label}{proj.hasRetainer ? ' (retainer)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>MONTH</label>
          <select value={bsMonth}
            onChange={e => { setBsMonth(Number(e.target.value)); if (bsProjId) lookupHoursInvoice(bsProjId, Number(e.target.value), bsYear); }}
            style={inp}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>YEAR</label>
          <select value={bsYear}
            onChange={e => { setBsYear(Number(e.target.value)); if (bsProjId) lookupHoursInvoice(bsProjId, bsMonth, Number(e.target.value)); }}
            style={inp}>
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Conflict banner — another draft on this project for a different month */}
      {bsDraftConflict && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fff', borderLeft: '4px solid #D4007A', border: '0.5px solid #E0E0DC', borderLeftWidth: 4, borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 8, height: 8, background: '#D4007A', borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600, color: '#111' }}>{bsDraftConflict.project_label}</span>
            <span style={{ color: '#444' }}> already has a draft in progress for {['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][bsDraftConflict.month]} {bsDraftConflict.year}.</span>
            {/* The gate is invisible without this line (2026-09-04 —
                Cimon built expense-group lines under this banner and
                lost them: autosave is disabled while it shows). */}
            <div style={{ color: '#C8322A', fontWeight: 600, marginTop: 2 }}>
              Nothing on this form saves while this banner is showing — open that draft, or send/delete it first.
            </div>
          </div>
          <button onClick={() => loadDraft(bsDraftConflict)}
            style={{ padding: '6px 12px', fontSize: 11, background: '#fff', color: '#D4007A', border: '1px solid #D4007A', borderRadius: 5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Open that draft
          </button>
        </div>
      )}

      {/* Saved-record banner — solid color dot indicates status. Since
          2026-07-20 it also names the working summary's identity + how
          many documents are tagged to it: an entire day of "where did my
          invoices go" debugging came down to the session silently working
          on a DIFFERENT summary row than the one the documents belong to.
          The identity line makes that visible at a glance. */}
      {bsSavedRecord && (() => {
        const sid = String(bsSavedRecord.id || '');
        const tp = (proposals || []).filter(p => String(p.billedOnSummaryId || p.billed_on_summary_id || '') === sid).length;
        const ti = (invoices || []).filter(inv => String(inv.billedOnSummaryId || inv.billed_on_summary_id || '') === sid).length;
        return (
          <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fff', border: '0.5px solid #E0E0DC', borderLeft: '4px solid ' + (bsSavedRecord.status === 'paid' ? '#1A5C38' : '#F5C800'), borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 8, height: 8, background: bsSavedRecord.status === 'paid' ? '#1A5C38' : '#F5C800', borderRadius: '50%', flexShrink: 0 }} />
            <span style={{ fontWeight: 600, color: '#111', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{bsSavedRecord.status}</span>
            {bsSavedRecord.sent_at && <span style={{ color: '#666' }}>Sent {fmtDate(bsSavedRecord.sent_at)}</span>}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
              Summary {sid.slice(0, 8)} · {tp} tagged proposal{tp === 1 ? '' : 's'} · {ti} tagged invoice{ti === 1 ? '' : 's'}
            </span>
            {bsSavedRecord.status === 'sent' && (
              <button onClick={() => setBsReopenConfirm(true)}
                title="Flip this sent summary back to a draft so it can be revised and re-sent. Blocked once any payment is recorded against the package."
                style={{ padding: '6px 12px', fontSize: 11, background: '#fff', color: '#111', border: '0.5px solid #D8D7D2', borderRadius: 5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                Reopen as draft…
              </button>
            )}
          </div>
        );
      })()}

      {/* Official-send confirm (2026-07-27) — Send Package + Mark as Sent
          are real sends: they stamp sent_at, tag every bundled document,
          archive the as-sent cover (package path), and draw the retainer
          on retainer projects. Neither had a confirmation before; use
          Preview ZIP to look without sending. */}
      {bsSendConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setBsSendConfirm(null)}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: 460, maxWidth: '92vw', border: '0.5px solid #D8D7D2' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#111', marginBottom: 10 }}>
              {bsSendConfirm === 'package'
                ? 'Send the ' + bsMonthName + ' ' + bsYear + ' package?'
                : 'Mark ' + bsMonthName + ' ' + bsYear + ' as sent?'}
            </div>
            <div style={{ fontSize: 12, color: '#444', lineHeight: 1.55, marginBottom: 14 }}>
              This is the official send: the summary is marked sent, every bundled
              invoice and proposal is tagged to this package
              {bsSendConfirm === 'package' ? ', and the as-sent cover is archived to Send History' : ''}.
              {projectHasRetainer && bsSubtotal > 0 && (
                <span> It also records the retainer draw of{' '}
                  <span style={{ fontWeight: 600, color: '#111' }}>
                    {bsSubtotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </span>.
                </span>
              )}
              <div style={{ marginTop: 8, color: '#8A6D00' }}>
                Just need to look at the package? Cancel and use ↓ Preview ZIP —
                it builds the same file without sending anything.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setBsSendConfirm(null)}
                style={{ padding: '8px 14px', fontSize: 12, background: '#fff', color: '#666', border: '0.5px solid #D8D7D2', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const which = bsSendConfirm;
                  setBsSendConfirm(null);
                  if (which === 'package') sendPackage();
                  else markAsSent();
                }}
                style={{ padding: '8px 14px', fontSize: 12, background: '#111', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                {bsSendConfirm === 'package' ? 'Send package' : 'Mark as sent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen-as-draft confirm (2026-07-20) — the sanctioned revision
          path for a sent-but-unpaid package. */}
      {bsReopenConfirm && bsSavedRecord && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { if (!bsReopenBusy) setBsReopenConfirm(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: 460, maxWidth: '92vw', border: '0.5px solid #D8D7D2' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#111', marginBottom: 10 }}>
              Reopen {bsMonthName} {bsYear} as a draft?
            </div>
            <div style={{ fontSize: 12, color: '#444', lineHeight: 1.55, marginBottom: 14 }}>
              Everything bundled on this package comes back onto the workbench for
              editing, and the next Send Package writes a fresh as-sent version
              (the one already archived stays in Send History). Any retainer draw
              from this send is removed now and re-created at the corrected amount
              when you re-send.
              <div style={{ marginTop: 8, color: '#8A6D00' }}>
                Only reopen if the client has not paid against this package —
                recorded payments block this automatically.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setBsReopenConfirm(false)} disabled={bsReopenBusy}
                style={{ padding: '8px 14px', fontSize: 12, background: '#fff', color: '#666', border: '0.5px solid #D8D7D2', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={reopenAsDraft} disabled={bsReopenBusy}
                style={{ padding: '8px 14px', fontSize: 12, background: '#111', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', opacity: bsReopenBusy ? 0.6 : 1 }}>
                {bsReopenBusy ? 'Reopening…' : 'Reopen as draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Design Services */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Design Services</div>
        {bsHoursLoading ? (
          <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Looking up finalized invoice…</div>
        ) : bsHoursInvoice?.status === 'Final' ? (
          <div style={{ fontSize: 11, color: '#1A5C38', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            {/* Total hours only (2026-09-04, Cimon: "on the billing summary
                page, just list the total hours") — the per-category count
                read as stage-of-design detail that no longer matches
                person-grouped clients. */}
            <span>✓ Loaded from {bsHoursInvoice.invoice_number} · {(parseFloat(bsHoursInvoice.total_hours) || 0).toFixed(1)} hrs · {fmtFull(parseFloat(bsHoursInvoice.total_amount) || 0)}</span>
            {/* Refresh narratives — 2026-06-29 per Cimon. Re-runs
                lookupHoursInvoice against the DB so edits made on the
                Designer Hours tab AFTER this Billing Summary was opened
                flow into the cover doc without having to toggle the
                project picker to force a re-fetch. */}
            <button
              type="button"
              onClick={() => lookupHoursInvoice(bsProjId, bsMonth, bsYear)}
              title="Re-read the latest hours invoice from the database — picks up narrative edits made on Designer Hours"
              style={{ fontSize: 10, padding: '3px 8px', border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#555', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Refresh narratives
            </button>
          </div>
        ) : bsProjId ? (
          <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
            No finalized hours invoice for {bsSelProj?.label} {bsMonthName} {bsYear} — enter manually or finalize the hours invoice first under Designer Hours.
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 12 }}>
          <div>
            <label style={lbl}>HOURS</label>
            <input type="number" step="0.1" value={bsDesignHours}
              onChange={e => setBsDesignHours(e.target.value)}
              style={{ ...inp, width: 90 }} />
          </div>
          <div>
            <label style={lbl}>AMOUNT</label>
            <input type="number" step="0.01" value={bsDesignAmount}
              onChange={e => setBsDesignAmount(e.target.value)}
              style={{ ...inp, width: 160 }} />
          </div>
        </div>
        {bsHoursNarratives.length > 0 && (
          <div style={{ marginTop: 14, padding: '10px 12px', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
              Hourly narratives — will appear under Design Services on the PDF
            </div>
            {bsHoursNarratives.map((n, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#111', marginBottom: 2 }}>{n.category} · {n.hours} hrs · {fmtFull(n.amount)}</div>
                <div style={{ fontSize: 11, color: '#555', whiteSpace: 'pre-wrap' }}>{n.narrative}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Additional Services */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Additional Services</div>
        {bsAdditionalServices.map((svc, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input placeholder="Service name" value={svc.name}
              onChange={e => { const a = [...bsAdditionalServices]; a[i] = {...a[i], name: e.target.value}; setBsAdditionalServices(a); }}
              style={{ ...inp, flex: 1 }} />
            <input type="number" step="0.01" placeholder="Amount" value={svc.amount}
              onChange={e => { const a = [...bsAdditionalServices]; a[i] = {...a[i], amount: e.target.value}; setBsAdditionalServices(a); }}
              style={{ ...inp, width: 140 }} />
            <button onClick={() => setBsAdditionalServices(bsAdditionalServices.filter((_,j) => j !== i))} style={rmBtn}>✕</button>
          </div>
        ))}
        <button onClick={() => setBsAdditionalServices([...bsAdditionalServices, { name: '', amount: '' }])} style={addBtn}>
          + Add charge
        </button>
      </div>

      {/* Project Expenses — free-text only. Receipt-backed reimbursables
          live in the Reimbursable Expenses picker block below (2026-05-18);
          these rows remain for the rare unreceipted entry (stipend,
          narrative-only line). */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Project Expenses</div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>For unreceipted entries. Receipt-backed reimbursables: pick them from the Reimbursable Expenses block below.</div>
        {bsExpenses.map((exp, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input placeholder="Description" value={exp.description}
              onChange={e => { const a = [...bsExpenses]; a[i] = {...a[i], description: e.target.value}; setBsExpenses(a); }}
              style={{ ...inp, flex: 1 }} />
            <input type="number" step="0.01" placeholder="Amount" value={exp.amount}
              onChange={e => { const a = [...bsExpenses]; a[i] = {...a[i], amount: e.target.value}; setBsExpenses(a); }}
              style={{ ...inp, width: 140 }} />
            <button onClick={() => setBsExpenses(bsExpenses.filter((_,j) => j !== i))} style={rmBtn}>✕</button>
          </div>
        ))}
        <button onClick={() => setBsExpenses([...bsExpenses, { description: '', amount: '' }])} style={addBtn}>
          + Add expense
        </button>
      </div>

      {/* Reimbursable Expenses picker — receipts attach directly here (2026-05-18).
          Replaces the deprecated BillToClientModal flow which bundled receipts
          into a separate reimbursable invoice that then had to be auto-included
          on the summary via period-window filtering. Now: tick the receipts
          you want on this summary, save the draft, send the package. Receipt
          descriptions are read LIVE from each receipt's notes/category/vendor
          at render time — edit the notes on the Receipts tab to update what
          the client sees on next render. */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 14 }} data-testid="bs-reimbursable-picker">
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Reimbursable Expenses for this period</div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
          Approved <code style={{ fontSize: 11 }}>project_reimbursable</code> receipts for this project, not yet attached to a sent billing summary. Editable on the Receipts tab — descriptions read live, no snapshot here.
        </div>
        {bsReimbursableReceiptsAll.length === 0 ? (
          <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', padding: '6px 0' }}>
            No untagged reimbursable receipts available for this project. Approve a project_reimbursable receipt on the Receipts tab and it will appear here.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '0.5px solid #E0E0DC', color: '#888' }}>
                <th style={{ padding: '6px 4px', textAlign: 'left', width: 36 }}></th>
                <th style={{ padding: '6px 4px', textAlign: 'left', width: 90 }}>DATE</th>
                <th style={{ padding: '6px 4px', textAlign: 'left' }}>VENDOR</th>
                <th style={{ padding: '6px 4px', textAlign: 'left' }}>DESCRIPTION</th>
                <th style={{ padding: '6px 4px', textAlign: 'right', width: 90 }}>COST</th>
                <th style={{ padding: '6px 4px', textAlign: 'right', width: 110 }}>CLIENT (+ MARKUP)</th>
              </tr>
            </thead>
            <tbody>
              {bsReimbursableReceiptsAll.map(function(r) {
                var checked = bsReimbursableSelections.has(r.id);
                var cost = parseFloat(r.totalAmount) || 0;
                var amt  = applyReimbursableMarkup(cost, receiptMarkupPct(r));
                var desc = receiptSummaryDescription(r);
                // Grouped receipts stay legible where they live (2026-09-04,
                // Cimon's rule: the bookkeeper must be able to see which
                // receipts were grouped together). The row dims and names
                // its line; untick still works (untick = don't bill, which
                // also takes it off the line).
                var grp = (bsExpensesDisplay.groups || []).find(function(g) {
                  return (g.receiptIds || []).indexOf(r.id) >= 0;
                });
                return (
                  <tr key={r.id}
                      data-testid={"bs-reimb-row-" + r.id}
                      style={{ borderBottom: '0.5px solid #F0F0EE', background: checked ? '#FAFAF8' : '#fff', color: grp ? '#999' : undefined }}>
                    <td style={{ padding: '6px 4px' }}>
                      <input type="checkbox"
                             data-testid={"bs-reimb-cb-" + r.id}
                             checked={checked}
                             onChange={function() {
                               setBsReimbursableSelections(function(prev) {
                                 var next = new Set(prev);
                                 if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                                 return next;
                               });
                               // Untick = not billed, so it cannot stay on a
                               // combined line. Drops it from whichever line
                               // holds it rather than leaving a ghost member
                               // that prints nothing.
                               if (bsReimbursableSelections.has(r.id)) {
                                 setBsExpensesDisplay(function(prev) {
                                   var gs = prev.groups || [];
                                   if (!gs.some(function(g) { return g.receiptIds.indexOf(r.id) >= 0; })) return prev;
                                   return Object.assign({}, prev, {
                                     groups: gs.map(function(g) {
                                       return Object.assign({}, g, {
                                         receiptIds: g.receiptIds.filter(function(id) { return id !== r.id; }),
                                       });
                                     }),
                                   });
                                 });
                               }
                             }} />
                    </td>
                    <td style={{ padding: '6px 4px', color: grp ? '#999' : '#555' }}>{r.receiptDate || '—'}</td>
                    <td style={{ padding: '6px 4px', color: grp ? '#999' : '#111' }}>{r.vendorName || '—'}</td>
                    <td style={{ padding: '6px 4px', color: grp ? '#999' : '#111' }}
                        title="Edit description on Receipts tab — reads from receipt's notes / category / vendor live at render time"
                        data-testid={"bs-reimb-desc-" + r.id}>
                      {desc}
                      {grp && (
                        <span data-testid={"bs-reimb-grouped-" + r.id}
                              style={{ fontSize: 10, color: '#888', fontStyle: 'italic' }}>
                          {' '}· on line “{(grp.label || '').trim() || DEFAULT_CONSOLIDATED_EXPENSES_LABEL}”
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: '#888' }}>{fmtFull(cost)}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: '#111', fontWeight: 500 }}>{fmtFull(amt)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '0.5px solid #E0E0DC', fontWeight: 600 }}>
                <td colSpan={5} style={{ padding: '8px 4px', textAlign: 'right', color: '#555' }}>Selected total ({bsReimbursableSelections.size}):</td>
                <td style={{ padding: '8px 4px', textAlign: 'right', color: '#111' }} data-testid="bs-reimb-total">{fmtFull(bsReimbursableTotal)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Statement presentation (2026-08-23 billing simplification).
          Consolidation collapses PROJECT EXPENSES on the client's cover —
          receipt lines + manual expense rows — into ONE line. Presentation
          only: totals and retainer math are identical; per-receipt detail
          stays in Gilda for backup-on-request. */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 20px', marginBottom: 14 }} data-testid="bs-expenses-display">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#111', cursor: 'pointer' }}>
          <input type="checkbox"
                 data-testid="bs-consolidate-expenses"
                 checked={!!bsExpensesDisplay.consolidate}
                 onChange={e => setBsExpensesDisplay(prev => ({ ...prev, consolidate: e.target.checked }))} />
          <span style={{ fontWeight: 600 }}>Consolidate expenses on the statement — one line, no itemized rows</span>
        </label>
        {bsExpensesDisplay.consolidate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingLeft: 24 }}>
            <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>Statement line reads:</span>
            <input value={bsExpensesDisplay.label}
                   data-testid="bs-consolidated-label"
                   placeholder={DEFAULT_CONSOLIDATED_EXPENSES_LABEL}
                   onChange={e => setBsExpensesDisplay(prev => ({ ...prev, label: e.target.value }))}
                   // Enter commits (2026-09-04). Typing already autosaves on
                   // the 1.5s debounce, but there was no way to SAY you were
                   // done — Enter did nothing and the only "Saved" marker sits
                   // in the action bar at the far bottom of the pane.
                   onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                   style={{ ...inp, flex: 1, maxWidth: 360 }} />
          </div>
        )}

        {/* Expense groups (2026-08-30, Cimon: "combine some receipts —
            airline and hotel — but keep storage and moving on their own
            line"). Middle ground between itemized and the single
            consolidated line: each group prints as ONE named row summing
            its receipts; unassigned receipts stay itemized. Presentation
            only — same totals, same retainer draw. Hidden while
            consolidate is on (the single line folds everything anyway). */}
        {!bsExpensesDisplay.consolidate && bsReimbursableSelections.size > 0 && (
          <div data-testid="bs-expense-groups" style={{ marginTop: 12, paddingTop: 12, borderTop: '0.5px solid #E0E0DC' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>Combine receipts into named lines</span>
              <div style={{ flex: 1 }} />
              {/* Save state, here rather than only in the action bar at the
                  far bottom of the pane (2026-09-04, Cimon: "there's no way
                  to complete the task"). Labels autosave as you type, but
                  with the only confirmation off-screen the edit felt
                  provisional — nothing said it had landed. */}
              {bsLastSavedAt && (
                <span data-testid="bs-expense-groups-saved"
                      style={{ fontSize: 10.5, color: '#1A5C38', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  Saved {bsLastSavedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
            {(bsExpensesDisplay.groups || []).length === 0 && (
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                No combined lines yet — every ticked receipt prints as its own row.
              </div>
            )}
            {(bsExpensesDisplay.groups || []).map(g => {
              // Resolve members from the LINE, not from the tick set
              // (2026-09-04, Cimon: "if I deselect a receipt in order to
              // create a new grouping, it removes it from the group I just
              // created"). Ticks mean "bill this"; membership means "on this
              // line". Filtering members by ticks made a finished line empty
              // itself the moment she cleared ticks to build the next one.
              // The two are kept in sync instead: adding ticks, unticking
              // removes from its line.
              const members = buildSummaryReceiptLines(g.receiptIds, receipts);
              return (
                <div key={g.id} style={{ border: '0.5px solid #E0E0DC', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input value={g.label}
                           data-testid={'bs-expense-group-label-' + g.id}
                           placeholder="Line label — what the client reads"
                           onChange={e => setBsExpensesDisplay(prev => ({
                             ...prev,
                             groups: (prev.groups || []).map(x => x.id === g.id ? { ...x, label: e.target.value } : x),
                           }))}
                           onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                           style={{ ...inp, flex: 1, maxWidth: 320 }} />
                    <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                      {members.length} receipt{members.length === 1 ? '' : 's'}
                    </span>
                    <button type="button"
                            title="Remove this line — its receipts go back to their own rows"
                            onClick={() => setBsExpensesDisplay(prev => ({
                              ...prev,
                              groups: (prev.groups || []).filter(x => x.id !== g.id),
                            }))}
                            style={{ border: 'none', background: 'transparent', color: '#C8322A', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                  </div>
                  {members.length === 0 ? (
                    <div style={{ fontSize: 10.5, color: '#888', marginTop: 6 }}>
                      No receipts on this line — tick some below and add them.
                    </div>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      {members.map(ln => (
                        <div key={ln.receiptId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#555', marginBottom: 2 }}>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ln.description}>
                            {ln.description} · {fmtFull(ln.amount)}
                          </span>
                          <button type="button"
                                  title="Take this receipt off the line — back to its own row"
                                  onClick={() => setBsExpensesDisplay(prev => ({
                                    ...prev,
                                    groups: (prev.groups || []).map(x => x.id === g.id
                                      ? { ...x, receiptIds: x.receiptIds.filter(id => id !== ln.receiptId) } : x),
                                  }))}
                                  style={{ border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Build a line: tick receipts, name it, add it. Only receipts
                not already on a line are offered, so a receipt can never
                land on two lines (applyExpenseGroupsToReceiptLines would
                silently drop the second claim anyway). */}
            {(() => {
              const unassigned = buildSummaryReceiptLines(Array.from(bsReimbursableSelections), receipts)
                .filter(ln => !(bsExpensesDisplay.groups || []).some(g => g.receiptIds.indexOf(ln.receiptId) >= 0));
              if (unassigned.length === 0) {
                return (
                  <div style={{ fontSize: 11, color: '#888' }}>
                    Every ticked receipt is on a line above.
                  </div>
                );
              }
              return (
                <div data-testid="bs-expense-group-builder"
                     style={{ borderTop: '0.5px dashed #E0E0DC', paddingTop: 8, marginTop: 4 }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>
                    Tick the receipts that belong together, name the line, then add it.
                  </div>
                  {unassigned.map(ln => (
                    <label key={ln.receiptId}
                           style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 3, cursor: 'pointer' }}>
                      <input type="checkbox"
                             data-testid={'bs-group-pick-' + ln.receiptId}
                             checked={bsGroupPicks.has(ln.receiptId)}
                             onChange={() => setBsGroupPicks(prev => {
                               const next = new Set(prev);
                               if (next.has(ln.receiptId)) next.delete(ln.receiptId); else next.add(ln.receiptId);
                               return next;
                             })} />
                      <span style={{ flex: 1, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={ln.description}>
                        {ln.description} · {fmtFull(ln.amount)}
                      </span>
                    </label>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input value={bsGroupDraftLabel}
                           data-testid="bs-new-group-label"
                           placeholder="Name this line — e.g. Airline expenses"
                           onChange={e => setBsGroupDraftLabel(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter' && bsGroupPicks.size > 0) { e.preventDefault(); addGroupFromPicks(); } }}
                           style={{ ...inp, flex: 1, maxWidth: 320 }} />
                    <button type="button"
                            data-testid="bs-create-expense-group"
                            disabled={bsGroupPicks.size === 0}
                            onClick={addGroupFromPicks}
                            style={{ height: 26, padding: '0 12px', borderRadius: 4,
                                     border: '0.5px solid ' + (bsGroupPicks.size ? '#111' : '#D8D7D2'),
                                     background: bsGroupPicks.size ? '#111' : '#fff',
                                     color: bsGroupPicks.size ? '#fff' : '#bbb',
                                     fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                                     cursor: bsGroupPicks.size ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
                      Add line{bsGroupPicks.size ? ' (' + bsGroupPicks.size + ')' : ''}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Calculation */}
      <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{projectHasRetainer ? 'Retainer Calculation' : 'Amount Due'}</div>
          {bsSelProj && <div style={{ fontSize: 11, color: '#888' }}>{projectHasRetainer ? 'Project on retainer' : 'Hourly billing — no retainer'}</div>}
        </div>
        {[
          { label: 'Design Services',    value: bsDesignAmt },
          { label: 'Additional Services', value: bsAddlTotal },
          { label: 'Project Expenses',    value: bsExpTotal  },
        ].map(row => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, color: '#555' }}>
            <span>{row.label}</span>
            <span>{fmtFull(row.value)}</span>
          </div>
        ))}
        {/* Retainer-only: selected reimbursable receipts fold into the asking
            amount via bsReimbursableTotal (already part of bsSubtotal via
            the line 405 formula). One inline row showing the rolled-up total
            so the bookkeeper sees what's pulling into the subtotal without
            scrolling to the picker section. Hidden for non-retainer projects
            and when no receipts are selected. The picker block below renders
            the per-receipt detail. */}
        {projectHasRetainer && bsReimbursableTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, color: '#555' }}>
            <span>Reimbursable expenses ({bsReimbursableSelections.size} receipt{bsReimbursableSelections.size === 1 ? '' : 's'})</span>
            <span>{fmtFull(bsReimbursableTotal)}</span>
          </div>
        )}
        <div style={{ borderTop: '1px solid #ddd', paddingTop: 10, marginTop: 6 }}>
          {projectHasRetainer ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
                <span>Subtotal</span>
                <span>{fmtFull(bsSubtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#555', marginBottom: 4 }}>
                <span>Current Retainer Balance</span>
                <span>{fmtFull(bsCurrentRetainer)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13, marginBottom: 12 }}>
                <span>New Retainer Balance</span>
                <span style={{ color: '#111' }}>{fmtFull(bsNewRetainer)}</span>
              </div>

              {/* Editable retainer request — what will appear on the
                  client's PDF. Default is auto-computed ($60K when balance
                  drops below threshold, else $0). Bookkeeper can override.
                  Setting to 0 suppresses the line entirely on the PDF.
                  Currency formatted on render and on blur via toLocaleString;
                  while focused the user can type freely (digits + commas +
                  dot all accepted on parse). Underlying state stays a
                  number — bsRetainerRequestOverride. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, fontWeight: 600, marginBottom: 8, paddingTop: 8, borderTop: '0.5px dashed #E0E0DC' }}>
                <span>Retainer Request</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {bsRetainerRequestOverride != null && bsRetainerRequestOverride !== bsAutoRetainerRequest && (
                    <button
                      onClick={() => setBsRetainerRequestOverride(null)}
                      title="Reset to auto-computed default"
                      style={{ fontSize: 10, color: '#888', background: 'none', border: '0.5px solid #D0D0CC', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
                      Reset
                    </button>
                  )}
                  <RetainerRequestInput
                    value={bsRetainerRequest}
                    onChange={n => setBsRetainerRequestOverride(n)}
                  />
                </div>
              </div>
              {bsRetainerRequest > 0 && (
                <div style={{ fontSize: 10, color: '#888', fontStyle: 'italic', marginTop: 4, marginBottom: 6, textAlign: 'right' }}>
                  Retainer is requested whenever the current balance falls below ${bsRetainerThreshold.toLocaleString()}.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15, paddingTop: 8, borderTop: '1px solid #111' }}>
                <span>AMOUNT DUE</span>
                <span style={{ color: '#1a1a1a' }}>{fmtFull(bsTotalDue)}</span>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15 }}>
              <span>TOTAL DUE</span>
              <span style={{ color: '#1a1a1a' }}>{fmtFull(bsSubtotal)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Procurement — Pending Proposals (auto-populated, opt-out)
          Sent proposals delivered via the billing-summary path that haven't
          yet been tagged onto a sent summary. No month/year filter — they
          stay pending until the bookkeeper sends or excludes them. */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Procurement — Pending Proposals</div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11, color: '#888' }}>{bsProposalsList.length} proposal{bsProposalsList.length === 1 ? '' : 's'}</div>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>Sent proposals delivered via the billing-summary path. Off-cycle proposals are excluded by design. Tagged on send so they don&rsquo;t reappear next month.</div>

        {bsProposalsList.length === 0 ? (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: '#bbb', fontSize: 12, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
            No pending proposals to bundle for this project.
          </div>
        ) : (
          <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#FAFAF8' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}></th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Ref</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Name</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Sent date</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Deposit req.</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {bsProposalsList.map(row => {
                  const p = row.proposal;
                  const isPreviewing = bsPreviewSel.type === 'proposal' && bsPreviewSel.id === p.id;
                  return (
                    <tr key={p.id} style={{ borderTop: '0.5px solid #EFEFEC', background: isPreviewing ? '#fff7e0' : '#fff' }}>
                      <td style={{ padding: '8px 10px', width: 30 }}>
                        <button
                          onClick={() => setBsProposalsExcluded(prev => Object.assign({}, prev, { [p.id]: true }))}
                          title="Exclude from this billing summary"
                          style={{ background: 'none', border: '0.5px solid #E0E0DC', color: '#999', borderRadius: 3, width: 18, height: 18, cursor: 'pointer', fontSize: 10, lineHeight: 1, padding: 0 }}>
                          ×
                        </button>
                      </td>
                      <td
                        onClick={() => previewBundledProposal(p)}
                        title="Preview this proposal in the right pane"
                        style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, color: '#0066c0', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}>
                        {p.proposal_ref || '—'}
                      </td>
                      <td style={{ padding: '8px 10px', color: '#444' }}>{p.proposal_name || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <input
                          type="date"
                          value={row.displayDate}
                          onChange={e => setBsProposalDateOverrides(prev => Object.assign({}, prev, { [p.id]: e.target.value }))}
                          style={{ padding: '4px 8px', fontSize: 11, border: '0.5px solid #E0E0DC', borderRadius: 4, fontFamily: 'inherit', background: '#fff', outline: 'none' }} />
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: '#111' }}>{fmtMoney(row.totalDeposit)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: '#444' }}>{fmtMoney(row.totalClient)}</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '1px solid #111' }}>
                  <td colSpan={4} style={{ padding: '10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Totals</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#111' }}>{fmtMoney(bsProposalsDeposit)}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#111' }}>{fmtMoney(bsProposalsTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {bsProposalsOutstanding > 0 && (
          <div style={{ marginTop: 10, padding: '8px 12px', borderTop: '0.5px solid #E0E0DC', fontSize: 11, color: '#666' }}>
            <span style={{ fontStyle: 'italic' }}>{fmtMoney(bsProposalsOutstanding)} outstanding</span> across these proposals — deposit invoices not yet marked paid.
          </div>
        )}

        {Object.keys(bsProposalsExcluded).length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button onClick={() => setBsProposalsExcluded({})}
              style={{ padding: '4px 10px', fontSize: 11, background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, cursor: 'pointer', color: '#666', fontFamily: 'inherit' }}>
              ↻ Restore all excluded proposals
            </button>
          </div>
        )}
      </div>

      {/* Pending Invoices (auto-populated, opt-out)
          Untagged client invoices for this project. No month/year filter
          and no sent_via concept (invoices have no two-path equivalent —
          they're either tagged onto a summary or pending). */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Pending Invoices</div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11, color: '#888' }}>{bsInvoicesList.length} invoice{bsInvoicesList.length === 1 ? '' : 's'}</div>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>Untagged invoices for this project. Click ref to preview. Tagged on send so they don&rsquo;t reappear next month.</div>

        {bsInvoicesList.length === 0 ? (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: '#bbb', fontSize: 12, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
            No pending invoices to bundle for this project.
          </div>
        ) : (
          <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#FAFAF8' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}></th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Ref</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Name</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Type</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Sent</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {bsInvoicesList.map(row => {
                  const inv = row.invoice;
                  const isPreviewing = bsPreviewSel.type === 'invoice' && bsPreviewSel.id === inv.id;
                  const typeLabel = (inv.invoiceType || inv.invoice_type || 'deposit').replace('_', ' ');
                  return (
                    <tr key={inv.id} style={{ borderTop: '0.5px solid #EFEFEC', background: isPreviewing ? '#fff7e0' : '#fff' }}>
                      <td style={{ padding: '8px 10px', width: 30 }}>
                        <button
                          onClick={() => setBsInvoicesExcluded(prev => Object.assign({}, prev, { [inv.id]: true }))}
                          title="Exclude from this billing summary"
                          style={{ background: 'none', border: '0.5px solid #E0E0DC', color: '#999', borderRadius: 3, width: 18, height: 18, cursor: 'pointer', fontSize: 10, lineHeight: 1, padding: 0 }}>
                          ×
                        </button>
                      </td>
                      <td
                        onClick={() => previewBundledInvoice(inv)}
                        title="Preview this invoice in the right pane"
                        style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11, color: '#0066c0', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}>
                        {inv.invoiceRef || inv.invoice_ref || '—'}
                      </td>
                      <td style={{ padding: '8px 10px', color: '#111' }}>{invoiceFriendlyName(inv, proposals)}</td>
                      <td style={{ padding: '8px 10px', color: '#444', textTransform: 'capitalize' }}>{typeLabel}</td>
                      <td style={{ padding: '8px 10px', color: '#444' }}>{fmtDate(row.displayDate)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: '#111' }}>
                        {fmtMoney(row.amount)}
                        {row.paid > 0.005 && (
                          <div style={{ fontSize: 10, fontWeight: 400, color: '#888', whiteSpace: 'nowrap' }}>
                            of {fmtMoney(row.fullAsk)} · {fmtMoney(row.paid)} paid
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '1px solid #111' }}>
                  <td colSpan={5} style={{ padding: '10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#111' }}>{fmtMoney(bsInvoicesTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {Object.keys(bsInvoicesExcluded).length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button onClick={() => setBsInvoicesExcluded({})}
              style={{ padding: '4px 10px', fontSize: 11, background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, cursor: 'pointer', color: '#666', fontFamily: 'inherit' }}>
              ↻ Restore all excluded invoices
            </button>
          </div>
        )}
      </div>

      {/* Available Credits — apply client credits to this summary's
          procurement amount due. Picker mirrors the reimbursable receipts
          pattern: tick to apply, type the amount (defaults to the full
          available balance, capped). Writes 'applied_to_billing_summary'
          rows in client_credit_ledger on persist; the FK ON DELETE CASCADE
          guarantees the applied rows disappear if the summary is deleted,
          restoring the original credit's available balance automatically. */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 24 }} data-testid="bs-credits-picker">
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Available Credits</div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11, color: '#888' }}>{bsAvailableCreditsList.length} credit{bsAvailableCreditsList.length === 1 ? '' : 's'}</div>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>Apply this client&rsquo;s outstanding credits against the procurement amount due. Unchecking removes the application and restores the balance. &ldquo;Statement line&rdquo; is the wording printed beside the reference; the section heading above it stays categorical (&ldquo;Less: Deposit previously received&rdquo; for money already in hand, &ldquo;Less: Credit applied&rdquo; otherwise).</div>

        {bsAvailableCreditsList.length === 0 ? (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: '#bbb', fontSize: 12, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px dashed #E0E0DC', borderRadius: 6 }}>
            No credits available for this client.
          </div>
        ) : (
          <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#FAFAF8' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', width: 36 }}></th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', width: 110 }}>Date</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Reference</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', width: 200 }}>Statement line &mdash; prints under the heading</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', width: 120 }}>Available</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', width: 140 }}>Apply</th>
                </tr>
              </thead>
              <tbody>
                {bsAvailableCreditsList.map(function(c) {
                  var applied = bsAppliedCredits[c.id];
                  var isChecked = !!applied;
                  var cap = c.available;
                  return (
                    <tr key={c.id}
                        data-testid={"bs-credit-row-" + c.id}
                        style={{ borderTop: '0.5px solid #EFEFEC', background: isChecked ? '#FAFAF8' : '#fff' }}>
                      <td style={{ padding: '6px 10px' }}>
                        <input type="checkbox"
                               data-testid={"bs-credit-cb-" + c.id}
                               checked={isChecked}
                               disabled={!isChecked && cap <= 0}
                               onChange={function() {
                                 setBsAppliedCredits(function(prev) {
                                   var next = Object.assign({}, prev);
                                   if (next[c.id]) {
                                     delete next[c.id];
                                   } else {
                                     next[c.id] = { amount: cap, ledgerRowId: null,
                                                    label: creditPrintLabel(c.entryType) };
                                   }
                                   return next;
                                 });
                               }} />
                      </td>
                      <td style={{ padding: '6px 10px', color: '#555' }}>{fmtDate(c.entryDate)}</td>
                      <td style={{ padding: '6px 10px', color: '#111' }}>{c.reference || '—'}</td>
                      <td style={{ padding: '6px 10px' }}>
                        {isChecked ? (
                          <input type="text"
                                 data-testid={"bs-credit-label-" + c.id}
                                 value={applied.label != null ? applied.label : creditPrintLabel(c.entryType)}
                                 placeholder={creditPrintLabel(c.entryType)}
                                 onChange={function(e) {
                                   var v = e.target.value;
                                   setBsAppliedCredits(function(prev) {
                                     var next = Object.assign({}, prev);
                                     next[c.id] = Object.assign({}, next[c.id], { label: v });
                                     return next;
                                   });
                                 }}
                                 style={{ padding: '4px 8px', fontSize: 12, border: '0.5px solid #E0E0DC', borderRadius: 4, width: 190, background: '#fff', outline: 'none', fontFamily: 'inherit' }} />
                        ) : (
                          <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#1A5C38', fontWeight: 500 }}>{fmtMoney(cap)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                        {isChecked ? (
                          <input type="number" step="0.01" min="0" max={cap}
                                 data-testid={"bs-credit-amt-" + c.id}
                                 value={applied.amount}
                                 onChange={function(e) {
                                   var raw = parseFloat(e.target.value);
                                   var v = isFinite(raw) ? raw : 0;
                                   if (v < 0) v = 0;
                                   if (v > cap) v = cap;
                                   setBsAppliedCredits(function(prev) {
                                     var next = Object.assign({}, prev);
                                     next[c.id] = Object.assign({}, next[c.id], { amount: v });
                                     return next;
                                   });
                                 }}
                                 style={{ padding: '4px 8px', fontSize: 12, border: '0.5px solid #E0E0DC', borderRadius: 4, width: 110, textAlign: 'right', background: '#fff', outline: 'none', fontFamily: 'inherit' }} />
                        ) : (
                          <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '1px solid #111' }}>
                  <td colSpan={5} style={{ padding: '10px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Applied total</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#111' }} data-testid="bs-credits-applied-total">{fmtMoney(bsAppliedCreditsTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {bsAppliedCreditsTotal > 0 && (
          <div style={{ marginTop: 10, padding: '8px 12px', fontSize: 11, color: '#666', borderTop: '0.5px solid #E0E0DC' }}>
            <span>Procurement amount due before credits: <strong>{fmtMoney(bsProcAmountDueGross)}</strong>. After credits: <strong>{fmtMoney(bsProcAmountDueNet)}</strong>.</span>
            {bsCreditsExcess > 0 && (
              <div style={{ marginTop: 4, color: '#1A5C38', fontStyle: 'italic' }}>
                Additional {fmtMoney(bsCreditsExcess)} credit remains available for future billing.
              </div>
            )}
          </div>
        )}
      </div>

      {bsSummaryError && <div style={{ color: '#c62828', fontSize: 12, marginBottom: 12 }}>{bsSummaryError}</div>}

      {/* (The Payments card + Record Client Payment wizard mount moved to
          Billing History's package preview on 2026-07-02 — payments are
          recorded where they're reviewed. This workbench is build/send
          only.) */}
      {/* Draft-FIN warning modal (2026-07-01) — replaces the browser
          window.confirm per the app's Confirmation Modal Pattern. Lists
          each excluded draft FIN by NAME (invoice number in small type —
          Cimon's direction: names so she knows what items are referenced)
          with its amount. The safe action (cancel and Mark Ready) gets
          the filled primary treatment. */}
      {bsDraftFinWarn && bsDraftFinWarn.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 520, maxWidth: '94vw', background: '#FFFFFF', border: '0.5px solid #D8D7D2', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid #D8D7D2' }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#111111', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#C8322A', flexShrink: 0 }} />
                {bsDraftFinWarn.length === 1
                  ? 'One final invoice isn’t ready yet'
                  : bsDraftFinWarn.length + ' final invoices aren’t ready yet'}
              </div>
              <button style={{ border: 'none', background: 'transparent', color: '#888880', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
                onClick={function() { setBsDraftFinWarn(null); }} title="Close">×</button>
            </div>
            <div style={{ padding: '18px 20px' }}>
              <div style={{ fontSize: 13, color: '#111111' }}>
                {bsDraftFinWarn.length === 1 ? 'This final invoice is' : 'These final invoices are'} still {bsDraftFinWarn.length === 1 ? 'a draft' : 'drafts'}, so {bsDraftFinWarn.length === 1 ? 'it' : 'they'} will
                <strong style={{ fontWeight: 500 }}> not appear on this summary</strong> and
                <strong style={{ fontWeight: 500 }}> not be in the package</strong>{bsClient && bsClient.name ? ' ' + bsClient.name + ' receives' : ' the client receives'}:
              </div>
              <div style={{ border: '0.5px solid #D8D7D2', borderRadius: 4, overflow: 'hidden', margin: '14px 0' }}>
                {bsDraftFinWarn.map(function(inv, idx) {
                  return (
                    <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '9px 12px', borderTop: idx === 0 ? 'none' : '0.5px solid #EFEFEC' }}>
                      <span>
                        <span style={{ fontSize: 12, color: '#111111' }}>{invoiceFriendlyName(inv, proposals) || 'Final invoice'}</span>
                        <span style={{ fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#888880', marginLeft: 8 }}>Draft</span>
                        <span style={{ display: 'block', fontSize: 10, color: '#888880', marginTop: 1, fontFeatureSettings: '"tnum", "lnum"' }}>{inv.invoiceRef || inv.invoice_ref || ''}</span>
                      </span>
                      <span style={{ fontSize: 12, color: '#111111', fontFeatureSettings: '"tnum", "lnum"', whiteSpace: 'nowrap' }}>
                        {fmtMoney(parseFloat(inv.amountDue != null ? inv.amountDue : inv.amount_due) || 0)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: '#555552' }}>
                To include {bsDraftFinWarn.length === 1 ? 'it' : 'them'}, mark each one Ready first — Client Invoices → Final Invoices — then come back and send. Or send the package now and bill these next time.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '0.5px solid #D8D7D2' }}>
              <button style={{ padding: '6px 12px', border: '0.5px solid #D8D7D2', borderRadius: 5, background: '#FFFFFF', color: '#111111', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                onClick={function() { setBsDraftFinWarn(null); sendPackage({ skipDraftGate: true }); }}>
                Send without them
              </button>
              <button style={{ padding: '7px 18px', border: '1.5px solid #111111', borderRadius: 6, background: '#111111', color: '#FFFFFF', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                onClick={function() { setBsDraftFinWarn(null); }}>
                Cancel — I’ll mark them ready first
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STICKY ACTION BAR — pinned to bottom of left pane while scrolling */}
      <div style={{ position: 'sticky', bottom: 0, marginTop: 16, background: '#fff', borderTop: '0.5px solid #E0E0DC', padding: '12px 0', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', zIndex: 5 }}>
        <button onClick={saveDraft} disabled={!bsProjId}
          style={{ padding: '10px 22px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: !bsProjId ? 'not-allowed' : 'pointer', opacity: !bsProjId ? 0.5 : 1 }}>
          Save Draft
        </button>
        <div style={{ position: 'relative' }}>
          <button onClick={openDraftPicker}
            style={{ padding: '10px 18px', background: '#fff', color: '#111', border: '1px solid #D4007A', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Continue with Summary ▾
          </button>
          {bsDraftPickerOpen && (
            <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, background: '#fff', border: '1px solid #111', borderRadius: 7, boxShadow: '0 -4px 16px rgba(0,0,0,0.08)', minWidth: 320, maxWidth: 480, zIndex: 50 }}>
              <div style={{ padding: '10px 14px', borderBottom: '0.5px solid #E0E0DC', display: 'flex', alignItems: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#111', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Open a draft</div>
                <div style={{ flex: 1 }} />
                <button onClick={() => setBsDraftPickerOpen(false)}
                  style={{ padding: '2px 8px', fontSize: 11, background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, cursor: 'pointer', color: '#666' }}>×</button>
              </div>
              {bsDraftsList.length === 0 ? (
                <div style={{ padding: 16, fontSize: 12, color: '#888', textAlign: 'center' }}>No drafts in progress.</div>
              ) : (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {bsDraftsList.map(d => {
                    const periodName = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.month] + ' ' + d.year;
                    return (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer' }}
                        onClick={() => loadDraft(d)}>
                        <span style={{ width: 8, height: 8, background: '#F5C800', borderRadius: '50%', marginRight: 10, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{d.project_label}</div>
                          <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{periodName} · saved {fmtDate(d.updated_at)}</div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); deleteDraft(d.id); }}
                          title="Delete this draft"
                          style={{ padding: '4px 8px', background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, fontSize: 11, color: '#888', cursor: 'pointer' }}>×</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <button onClick={generateBillingSummary} disabled={bsSummaryGenerating || !bsProjId}
          style={{ padding: '10px 18px', background: '#fff', color: '#111', border: '1px solid #111', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: (bsSummaryGenerating || !bsProjId) ? 'not-allowed' : 'pointer', opacity: !bsProjId ? 0.5 : 1 }}>
          {bsSummaryGenerating ? 'Generating…' : '↓ Cover PDF'}
        </button>
        <button onClick={() => sendPackage({ previewOnly: true })} disabled={bsPackageGenerating || !bsProjId}
          title="Build the full package ZIP from current data WITHOUT sending — nothing is marked sent, tagged, archived, or drawn. The file is suffixed _preview."
          style={{ padding: '10px 18px', background: '#fff', color: '#444', border: '0.5px solid #D8D7D2', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: (bsPackageGenerating || !bsProjId) ? 'not-allowed' : 'pointer', opacity: !bsProjId ? 0.5 : 1 }}>
          {bsPackageGenerating ? 'Building…' : '↓ Preview ZIP'}
        </button>
        <button onClick={() => setBsSendConfirm('package')} disabled={bsPackageGenerating || !bsProjId}
          title="The OFFICIAL send: builds the package ZIP, marks every doc Sent, archives the as-sent cover, and draws the retainer. Confirmation follows."
          style={{ padding: '10px 18px', background: '#fff', color: '#111', border: '1px solid #111', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: (bsPackageGenerating || !bsProjId) ? 'not-allowed' : 'pointer', opacity: !bsProjId ? 0.5 : 1 }}>
          {bsPackageGenerating ? 'Building package…' : '↓ Send Package (ZIP)'}
        </button>
        {bsSavedRecord && bsSavedRecord.status === 'draft' && (
          <button onClick={() => setBsSendConfirm('mark_sent')}
            title="Official send without building the ZIP — marks the summary sent, tags bundled docs, draws the retainer. Confirmation follows."
            style={{ padding: '10px 18px', background: '#fff', color: '#1A5C38', border: '1px solid #1A5C38', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Mark as Sent
          </button>
        )}
        <div style={{ flex: 1 }} />
        {bsLastSavedAt && (
          <span style={{ fontSize: 11, color: '#1A5C38', fontWeight: 500 }}>
            Saved {bsLastSavedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
      </div>

      </div>{/* /LEFT PANE */}

      {/* RIGHT PANE — preview swaps based on bsPreviewSel */}
      <div style={{ position: 'sticky', top: 20, alignSelf: 'start', height: 'calc(100vh - 110px)', background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '8px 14px', borderBottom: '0.5px solid #E0E0DC', display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: '#666', minHeight: 32 }}>
          {bsPreviewSel.type === 'cover' ? (
            <>
              <span style={{ fontWeight: 600 }}>Cover preview{bsLivePdfPeriod ? ' · ' + bsLivePdfPeriod : ''}</span>
              {bsLivePdfLoading && <span style={{ color: '#888', fontStyle: 'italic' }}>Updating…</span>}
              {/* Gate visibility (2026-08-16): when the settled-state
                  gates are holding the render back, SAY SO — a silently
                  gated pane is indistinguishable from a broken one. */}
              {!bsLivePdfLoading && !!bsProjId
                && (bsHoursLoading || bsRecordLoadedKey !== bsProjId + '|' + bsYear + '|' + bsMonth)
                && <span style={{ color: '#888', fontStyle: 'italic' }}>Waiting for period data…</span>}
              {bsLivePdfError && <span style={{ color: '#c62828' }}>{bsLivePdfError}</span>}
            </>
          ) : (
            <>
              <button onClick={backToCover}
                style={{ padding: '3px 8px', fontSize: 11, background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, cursor: 'pointer', color: '#444', fontFamily: 'inherit' }}>
                ← Cover
              </button>
              <span style={{ fontWeight: 600 }}>
                {bsPreviewSel.type === 'proposal' ? 'Proposal preview' : 'Preview'}
              </span>
              {bsBundledPdfLoading && <span style={{ color: '#888', fontStyle: 'italic' }}>Loading…</span>}
              {bsBundledPdfError && <span style={{ color: '#c62828' }}>{bsBundledPdfError}</span>}
            </>
          )}
        </div>
        <div style={{ flex: 1, position: 'relative', background: '#fafafa' }}>
          {bsPreviewSel.type === 'cover' ? (
            !bsProjId ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
                Select a project to see the live preview.
              </div>
            ) : bsLivePdfUrl ? (
              <iframe title="Billing summary preview" src={bsLivePdfUrl}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: bsLivePdfError ? '#c62828' : '#999', fontSize: 12, padding: '0 32px', textAlign: 'center' }}>
                {bsLivePdfError
                  ? ('Preview failed: ' + bsLivePdfError)
                  : (bsLivePdfLoading ? 'Generating preview…' : 'Preview will appear after the next change.')}
              </div>
            )
          ) : (
            bsBundledPdfUrl ? (
              <iframe title="Bundled doc preview" src={bsBundledPdfUrl}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
                {bsBundledPdfLoading ? 'Generating…' : (bsBundledPdfError || 'No preview available.')}
              </div>
            )
          )}
        </div>
      </div>

    </div>
  );
}
